import { AlertCircle, ChevronLeft, ExternalLink, Loader2, Settings, Upload, Wand2, X, ZoomIn } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../../../../consts";
import authService from "../../../../services/AuthService";
import {
    listCostDefinitions, createCostDefinition,
    listFlagDefinitions, createFlagDefinition,
    listFieldDefinitions, createFieldDefinition,
    addBatchItem,
} from "../../services/catalogService";
import WalmartFeeSettingsModal from "./WalmartFeeSettingsModal";
import { estimateWalmartProfit, extractPriceFromPayload } from "../../utils/walmartFees";
import FlashPricerComponent from "./FlashPricerComponent";
import { useListingContentChecks, ContentIssuesBanner } from "./ListingContentChecks";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);

// Same upload pattern as mediaKitApi.uploadImage — lets sellers upload a
// file here instead of hunting down and pasting an image URL by hand.
const uploadWalmartImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("prefix", "walmart-item");
    const res = await authService.authenticatedFetch(`${API_BASE}/api/upload/image`, { method: "POST", body: fd });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Upload failed.");
    return data.url;
};
const MP_ITEM_VERSION = "5.0.20260501-19_21_29-api";
// Same extension ID your other MSP-extension integrations already message —
// replace with the shared constant if one already exists elsewhere in the app.
const EXT_ID = process.env.REACT_APP_MSP_EXTENSION_ID || 'elebkaoefjdagkbfienaoonkgifogkob';


const ImagePickerModal = ({ images, onSelect, onClose }) => {
    const [zoomUrl, setZoomUrl] = useState(null);

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[80vh] flex flex-col">
                <div className="bg-gradient-to-r from-purple-500 to-blue-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <h2 className="text-white font-bold text-lg">Pick an Image</h2>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
                </div>
                <div className="p-5 overflow-y-auto grid grid-cols-4 gap-3">
                    {images.map((url) => (
                        <div key={url} className="relative group">
                            <button type="button" onClick={() => onSelect(url)}
                                className="w-full rounded-xl overflow-hidden border-2 border-gray-200 hover:border-purple-500 transition">
                                <img src={url} alt="" className="w-full h-24 object-cover" />
                            </button>
                            <button type="button"
                                onClick={(e) => { e.stopPropagation(); setZoomUrl(url); }}
                                title="Preview full size"
                                className="absolute top-1 right-1 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition">
                                <ZoomIn size={14} />
                            </button>
                        </div>
                    ))}
                    {images.length === 0 && (
                        <p className="col-span-4 text-sm text-gray-400 text-center py-8">No images available for this product.</p>
                    )}
                </div>
            </div>

            {zoomUrl && (
                <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-8"
                    onClick={() => setZoomUrl(null)}>
                    <button onClick={() => setZoomUrl(null)}
                        className="absolute top-4 right-4 text-white/80 hover:text-white">
                        <X size={28} />
                    </button>
                    <img src={zoomUrl} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
                    <button
                        onClick={(e) => { e.stopPropagation(); onSelect(zoomUrl); }}
                        className="absolute bottom-6 px-5 py-2.5 bg-white text-purple-700 rounded-xl font-bold text-sm shadow-lg hover:bg-purple-50 transition">
                        Use This Image
                    </button>
                </div>
            )}
        </div>
    );
};

const ProductImage = ({ src, fallback = "📦", className = "" }) => {
    const [errored, setErrored] = useState(false);
    if (!src || errored) return <span className={`text-3xl ${className}`}>{fallback}</span>;
    return (
        <img src={src} alt="" onError={() => setErrored(true)}
            className={`object-contain rounded ${className}`} style={{ width: 56, height: 56 }} />
    );
};

/* ─── Country of origin: searchable, remembers last pick ─── */
const COUNTRIES = [
    "United States", "China", "Viet Nam", "India", "Mexico", "Canada", "Bangladesh", "Indonesia",
    "Taiwan", "Thailand", "Cambodia", "Turkey", "Italy", "Germany", "Pakistan", "Sri Lanka",
    "Malaysia", "Philippines", "South Korea", "Japan", "Brazil", "United Kingdom", "France",
    "Spain", "Poland", "Honduras", "Guatemala", "Portugal", "Netherlands", "Australia", "Hong Kong",
];
// Aliases for names sellers commonly type/expect vs. Walmart's exact enum
// spelling — corrected at submission time regardless of how it got into
// countryOfOrigin (picker selection, free-typed text, or AI-fill).
const COUNTRY_ALIASES = { "Vietnam": "Viet Nam", "South Korea": "Korea, South", "North Korea": "Korea, North" };
const LAST_COUNTRY_KEY = "wm_last_country_of_origin";

// ISO codes for the CountryPicker's own option list — needed only when the
// target schema field wants Walmart's "XX - Country Name" array format
// (e.g. WFS's TradeItem.countryOfOriginAssembly), not for MP_ITEM's plain
// string field.
const COUNTRY_CODES = {
    "United States": "US", "China": "CN", "Viet Nam": "VN", "India": "IN", "Mexico": "MX",
    "Canada": "CA", "Bangladesh": "BD", "Indonesia": "ID", "Taiwan": "TW", "Thailand": "TH",
    "Cambodia": "KH", "Turkey": "TR", "Italy": "IT", "Germany": "DE", "Pakistan": "PK",
    "Sri Lanka": "LK", "Malaysia": "MY", "Philippines": "PH", "South Korea": "KR", "Japan": "JP",
    "Brazil": "BR", "United Kingdom": "GB", "France": "FR", "Spain": "ES", "Poland": "PL",
    "Honduras": "HN", "Guatemala": "GT", "Portugal": "PT", "Netherlands": "NL", "Australia": "AU",
    "Hong Kong": "HK",
};

const RECENT_SUPPLIERS_KEY = "wm_recent_suppliers";
const MAX_RECENT_SUPPLIERS = 25;

const loadRecentSuppliers = () => {
    try {
        const raw = JSON.parse(localStorage.getItem(RECENT_SUPPLIERS_KEY));
        return Array.isArray(raw) ? raw : [];
    } catch {
        return [];
    }
};

// Bumps `name` to the front of the recency-ordered list (most-recently-used
// first), de-duping case-insensitively so "Acme" and "acme" don't both
// appear, and caps the stored history so it can't grow unbounded.
const recordSupplierUsed = (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    try {
        const existing = loadRecentSuppliers();
        const deduped = existing.filter((s) => s.toLowerCase() !== trimmed.toLowerCase());
        const next = [trimmed, ...deduped].slice(0, MAX_RECENT_SUPPLIERS);
        localStorage.setItem(RECENT_SUPPLIERS_KEY, JSON.stringify(next));
    } catch { /* non-fatal */ }
};

// Per-batch "last used" values (supplier + purchase date) — separate from
// the global recent-suppliers history above. That history is app-wide and
// just powers suggestions; this is scoped to a single batchId so that
// filling out item #2 of a shipment auto-inherits whatever was entered on
// item #1, without leaking across unrelated batches/shipments.
const lastValuesKey = (batchId) => `wm_batch_last_values_${batchId}`;
const loadBatchLastValues = (batchId) => {
    if (!batchId) return {};
    try {
        const raw = JSON.parse(localStorage.getItem(lastValuesKey(batchId)));
        return raw && typeof raw === "object" ? raw : {};
    } catch {
        return {};
    }
};
const saveBatchLastValue = (batchId, key, value) => {
    if (!batchId) return;
    try {
        const existing = loadBatchLastValues(batchId);
        localStorage.setItem(lastValuesKey(batchId), JSON.stringify({ ...existing, [key]: value }));
    } catch { /* non-fatal */ }
};

// Same searchable-dropdown UX as CountryPicker, but the option list is the
// seller's own supplier history (most-recently-used first) instead of a
// fixed list — a supplier typed once here shows up as a suggestion on
// every future item, ordered by how recently it was used.
const SupplierPicker = ({ value, onChange }) => {
    const [query, setQuery] = useState(value || "");
    const [open, setOpen] = useState(false);
    const [recent, setRecent] = useState(() => loadRecentSuppliers());

    useEffect(() => {
        setQuery(value || "");
    }, [value]);

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return recent.slice(0, 8);
        return recent.filter((s) => s.toLowerCase().includes(q)).slice(0, 8);
    }, [query, recent]);

    const select = (name) => {
        setQuery(name);
        onChange(name);
        recordSupplierUsed(name);
        setRecent(loadRecentSuppliers());
        setOpen(false);
    };

    return (
        <div className="relative">
            <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); onChange(e.target.value); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Type or pick a past supplier..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
            />
            {open && matches.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {matches.map((s, i) => (
                        <button key={s} onMouseDown={() => select(s)} className="w-full text-left px-3 py-2 text-sm hover:bg-teal-50">
                            {s}{i === 0 && !query.trim() && <span className="text-[10px] text-gray-400 ml-2">most recent</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const CountryPicker = ({ value, onChange }) => {
    const [query, setQuery] = useState(value || "");
    const [open, setOpen] = useState(false);
    const lastUsed = localStorage.getItem(LAST_COUNTRY_KEY);

    // query only initializes from value on mount — without this, external
    // updates (AI-fill, hydrateFromInitialItem on retry, etc.) set the
    // parent's countryOfOrigin state correctly but this input keeps showing
    // whatever it displayed at mount, since nothing re-syncs it afterward.
    useEffect(() => {
        setQuery(value || "");
    }, [value]);

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (lastUsed && !q) return [lastUsed, ...COUNTRIES.filter((c) => c !== lastUsed)].slice(0, 8);
        return COUNTRIES.filter((c) => c.toLowerCase().includes(q)).slice(0, 8);
    }, [query, lastUsed]);

    const select = (country) => {
        setQuery(country);
        onChange(country);
        localStorage.setItem(LAST_COUNTRY_KEY, country);
        setOpen(false);
    };

    return (
        <div className="relative">
            <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); onChange(e.target.value); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Type to search countries..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
            />
            {open && matches.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {matches.map((c) => (
                        <button key={c} onMouseDown={() => select(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-teal-50">
                            {c}{c === lastUsed && !query.trim() && <span className="text-[10px] text-gray-400 ml-2">last used</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ─── SKU Builder ─── */
const SKU_PATTERN_KEY = "wm_sku_builder_pattern";
const defaultSkuPattern = () => ({ parts: ["title3", "item_id"], separator: "-" });
const SKU_PART_OPTIONS = [
    { key: "item_id", label: "Item ID" },
    { key: "upc", label: "UPC" },
    { key: "gtin", label: "GTIN" },
    { key: "title3", label: "Title (first 3 letters)" },
    { key: "brand3", label: "Brand (first 3 letters)" },
    { key: "sell_price", label: "Sell Price" },
    { key: "cost_price", label: "Cost Price" },
    { key: "breakeven", label: "Breakeven Price" },
    { key: "max_cost", label: "Max Cost" },
    { key: "date", label: "Expiration Date (omitted if today/none)" },
    { key: "source", label: "Source / Supplier" },
    { key: "random4", label: "Random Digits (4)" },
];

export const buildSku = (pattern, ctx) => {
    const valueFor = (key) => {
        // Dynamic parts sourced from the seller's own cost/flag/custom-field
        // definitions — keys look like "cost:<id>", "field:<id>", "flag:<id>".
        if (key.startsWith("cost:")) {
            const val = ctx.costs?.[key.slice(5)];
            return val != null && val !== "" ? String(val).replace(".", "") : "";
        }
        if (key.startsWith("field:")) {
            const val = ctx.customFields?.[key.slice(6)];
            return val != null && val !== "" ? String(val).replace(/[^A-Za-z0-9]/g, "").toUpperCase() : "";
        }
        if (key.startsWith("flag:")) {
            const defId = key.slice(5);
            const def = (ctx.flagDefs || []).find((d) => d.id === defId);
            const active = (ctx.flags || []).includes(defId);
            return active && def ? def.name.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 4) : "";
        }
        switch (key) {
            case "item_id": return ctx.item_id || "";
            case "upc": return ctx.upc || "";
            case "gtin": return ctx.gtin || "";
            case "title3": return (ctx.title || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();
            case "brand3": return (ctx.brand || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();
            case "sell_price": return ctx.sell_price != null ? String(ctx.sell_price).replace(".", "") : "";
            case "cost_price": return ctx.cost_price != null ? String(ctx.cost_price).replace(".", "") : "";
            case "breakeven": return ctx.breakeven != null ? ctx.breakeven.toFixed(2).replace(".", "") : "";
            case "max_cost": return ctx.max_cost != null ? ctx.max_cost.toFixed(2).replace(".", "") : "";
            case "date": {
                if (!ctx.expiration_date) return ""; // no expiration set — omit entirely
                const today = new Date().toISOString().slice(0, 10);
                if (ctx.expiration_date === today) return ""; // today isn't meaningful info — omit
                return ctx.expiration_date.replace(/-/g, "");
            }
            case "source": return (ctx.source || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();
            case "random4": return String(Math.floor(1000 + Math.random() * 9000));
            default: return "";
        }
    };
    return pattern.parts.map(valueFor).filter(Boolean).join(pattern.separator || "").toUpperCase();
};

const SKU_PRESETS_KEY = "wm_sku_builder_presets";

const SkuBuilderModal = ({ pattern, onSave, onClose, ctx, costDefs = [], flagDefs = [], fieldDefs = [] }) => {
    const [local, setLocal] = useState(pattern);
    const preview = buildSku(local, ctx);

    // Your own cost/flag/custom-field definitions, offered as extra SKU parts.
    const dynamicOptions = useMemo(() => ([
        ...costDefs.filter((d) => d.name?.toLowerCase() !== "cost price").map((d) => ({ key: `cost:${d.id}`, label: `Cost: ${d.name}` })),
        ...fieldDefs.map((d) => ({ key: `field:${d.id}`, label: `Field: ${d.name}` })),
        ...flagDefs.map((d) => ({ key: `flag:${d.id}`, label: `Flag: ${d.name}` })),
    ]), [costDefs, fieldDefs, flagDefs]);
    const allPartOptions = [...SKU_PART_OPTIONS, ...dynamicOptions];

    const togglePart = (key) => setLocal((p) => ({
        ...p, parts: p.parts.includes(key) ? p.parts.filter((k) => k !== key) : [...p.parts, key],
    }));
    const movePart = (key, dir) => setLocal((p) => {
        const idx = p.parts.indexOf(key);
        const next = [...p.parts];
        const swapIdx = idx + dir;
        if (swapIdx < 0 || swapIdx >= next.length) return p;
        [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
        return { ...p, parts: next };
    });

    // Saved named "styles" — different part/separator combos you can
    // switch between, persisted across sessions.
    const [presets, setPresets] = useState(() => {
        try { return JSON.parse(localStorage.getItem(SKU_PRESETS_KEY)) || []; }
        catch { return []; }
    });
    const [presetName, setPresetName] = useState("");

    const saveAsPreset = () => {
        if (!presetName.trim()) return;
        const next = [...presets, { id: Date.now().toString(), name: presetName.trim(), pattern: local }];
        setPresets(next);
        localStorage.setItem(SKU_PRESETS_KEY, JSON.stringify(next));
        setPresetName("");
    };
    const deletePreset = (id) => {
        const next = presets.filter((p) => p.id !== id);
        setPresets(next);
        localStorage.setItem(SKU_PRESETS_KEY, JSON.stringify(next));
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[85vh] flex flex-col">
                <div className="bg-gradient-to-r from-teal-500 to-blue-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <h2 className="text-white font-bold text-lg">SKU Builder</h2>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
                </div>
                <div className="p-6 overflow-y-auto">
                    <div className="bg-gray-50 border-2 border-dashed border-teal-300 rounded-xl p-4 mb-4 text-center">
                        <p className="text-xs text-gray-400 mb-1">Preview</p>
                        <p className="text-xl font-bold tracking-widest font-mono text-teal-700 break-all">
                            {preview || <span className="text-gray-300 text-base">No parts selected</span>}
                        </p>
                    </div>

                    <label className="text-xs font-semibold text-gray-600 mb-2 block">Include (in order)</label>
                    <div className="space-y-1 mb-4">
                        {allPartOptions.map((opt) => {
                            const active = local.parts.includes(opt.key);
                            const idx = local.parts.indexOf(opt.key);
                            return (
                                <div key={opt.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 ${active ? "border-teal-500 bg-teal-50" : "border-gray-200"}`}>
                                    <button onClick={() => togglePart(opt.key)} className="flex-1 text-left text-sm font-semibold">
                                        {active && <span className="text-teal-600 mr-1">{idx + 1}.</span>}{opt.label}
                                    </button>
                                    {active && (
                                        <div className="flex gap-1">
                                            <button onClick={() => movePart(opt.key, -1)} className="text-xs text-gray-400 hover:text-gray-700">↑</button>
                                            <button onClick={() => movePart(opt.key, 1)} className="text-xs text-gray-400 hover:text-gray-700">↓</button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Separator</label>
                    <div className="flex gap-2 mb-5">
                        {["-", "_", ".", ""].map((s) => (
                            <button key={s || "none"} onClick={() => setLocal((p) => ({ ...p, separator: s }))}
                                className={`px-3 py-2 text-sm font-bold rounded-lg border-2 font-mono ${local.separator === s ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-500"}`}>
                                {s === "" ? "None" : s}
                            </button>
                        ))}
                    </div>

                    <div className="mb-5 pt-4 border-t border-gray-100">
                        <label className="text-xs font-semibold text-gray-600 mb-2 block">Saved Styles</label>
                        {presets.length === 0 && <p className="text-xs text-gray-400 mb-2">No saved styles yet.</p>}
                        <div className="space-y-1 mb-3">
                            {presets.map((p) => (
                                <div key={p.id} className="flex items-center gap-2">
                                    <button onClick={() => setLocal(p.pattern)}
                                        className="flex-1 text-left px-3 py-1.5 text-xs font-semibold bg-gray-50 hover:bg-teal-50 rounded-lg border border-gray-200">
                                        {p.name}
                                    </button>
                                    <button onClick={() => deletePreset(p.id)} className="text-red-400 hover:text-red-600 text-xs px-1">✕</button>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input value={presetName} onChange={(e) => setPresetName(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && saveAsPreset()}
                                placeholder="Style name (e.g. 'Prep Center A')"
                                className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                            <button onClick={saveAsPreset} disabled={!presetName.trim()}
                                className="px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold hover:bg-teal-100 disabled:opacity-50 whitespace-nowrap">
                                Save as New Style
                            </button>
                        </div>
                    </div>

                    <button onClick={() => { onSave(local); onClose(); }}
                        className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition">
                        Save Pattern
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Costs / Flags — backed by real cost_definitions / flag_definitions ─── */
const CostsPicker = ({ costDefs, selectedCosts, setSelectedCosts, onAddDef, autoFilled }) => {
    const [newName, setNewName] = useState("");
    const [adding, setAdding] = useState(false);

    const handleAdd = async () => {
        if (!newName.trim()) return;
        setAdding(true);
        try {
            const { definition } = await createCostDefinition(newName.trim());
            onAddDef(definition);
            setNewName("");
        } finally {
            setAdding(false);
        }
    };

    return (
        <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">Your Costs</label>
            {costDefs.length === 0 && <p className="text-xs text-gray-400 mb-2">No costs defined yet.</p>}
            {costDefs.filter((def) => def.id).map((def) => (
                <div key={def.id} className="flex items-center gap-2 mb-2">
                    <span className="flex-1 text-sm text-gray-700">{def.name}{autoFilled?.has(`cost:${def.id}`) && <BuylistBadge />}</span>
                    <div className="flex items-center gap-1">
                        <span className="text-gray-400 text-xs">$</span>
                        <input type="number" value={selectedCosts[def.id] ?? ""}
                            onChange={(e) => setSelectedCosts((prev) => ({ ...prev, [def.id]: e.target.value }))}
                            placeholder="0.00" className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-teal-500" />
                    </div>
                </div>
            ))}
            <div className="flex gap-2 mt-2">
                <input value={newName} onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                    placeholder="New cost name (e.g. Prep Fee)"
                    className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-teal-500" />
                <button onClick={handleAdd} disabled={adding || !newName.trim()}
                    className="px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold hover:bg-teal-100 disabled:opacity-50">
                    + Add
                </button>
            </div>
        </div>
    );
};

const FieldsPicker = ({ fieldDefs, selectedFields, setSelectedFields, onAddDef, autoFilled }) => {
    const [newName, setNewName] = useState("");
    const [adding, setAdding] = useState(false);

    const handleAdd = async () => {
        if (!newName.trim()) return;
        setAdding(true);
        try {
            const { definition } = await createFieldDefinition(newName.trim());
            onAddDef(definition);
            setNewName("");
        } finally {
            setAdding(false);
        }
    };

    return (
        <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">Your Custom Fields</label>
            {fieldDefs.length === 0 && <p className="text-xs text-gray-400 mb-2">No custom fields defined yet.</p>}
            {fieldDefs.map((def) => (
                <div key={def.id} className="flex items-center gap-2 mb-2">
                    <span className="flex-1 text-sm text-gray-700">{def.name}{autoFilled?.has(`field:${def.id}`) && <BuylistBadge />}</span>
                    <input value={selectedFields[def.id] ?? ""}
                        onChange={(e) => setSelectedFields((prev) => ({ ...prev, [def.id]: e.target.value }))}
                        placeholder="Value" className="w-32 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-teal-500" />
                </div>
            ))}
            <div className="flex gap-2 mt-2">
                <input value={newName} onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                    placeholder="New field name (e.g. Storage Bin)"
                    className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-teal-500" />
                <button onClick={handleAdd} disabled={adding || !newName.trim()}
                    className="px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold hover:bg-purple-100 disabled:opacity-50">
                    + Add
                </button>
            </div>
        </div>
    );
};

const FlagsPicker = ({ flagDefs, selectedFlagIds, setSelectedFlagIds, onAddDef }) => {
    const [newName, setNewName] = useState("");
    const [adding, setAdding] = useState(false);

    const toggle = (id) => setSelectedFlagIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

    const handleAdd = async () => {
        if (!newName.trim()) return;
        setAdding(true);
        try {
            const { definition } = await createFlagDefinition(newName.trim(), "#6b7280");
            onAddDef(definition);
            setSelectedFlagIds((prev) => [...prev, definition.id]);
            setNewName("");
        } finally {
            setAdding(false);
        }
    };

    return (
        <div>
            <label className="text-xs font-semibold text-gray-600 mb-2 block">Your Flags</label>
            {flagDefs.length === 0 && <p className="text-xs text-gray-400 mb-2">No flags defined yet.</p>}
            <div className="flex flex-wrap gap-1.5 mb-2">
                {flagDefs.map((def) => {
                    const active = selectedFlagIds.includes(def.id);
                    return (
                        <button key={def.id} onClick={() => toggle(def.id)}
                            style={active ? { backgroundColor: `${def.color}22`, color: def.color, borderColor: def.color } : {}}
                            className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition ${active ? "" : "border-gray-200 text-gray-400"}`}>
                            {def.name}
                        </button>
                    );
                })}
            </div>
            <div className="flex gap-2">
                <input value={newName} onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                    placeholder="New flag name"
                    className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-teal-500" />
                <button onClick={handleAdd} disabled={adding || !newName.trim()}
                    className="px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold hover:bg-teal-100 disabled:opacity-50">
                    + Add
                </button>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   Schema flattening — shape-agnostic, propagates required through
   optional containers, treats oneOf-required keys as required, skips
   MPItemFeedHeader/productIdentifiers/sku/country entirely, detects
   measure/unit pairs.
   ───────────────────────────────────────────────────────────── */
export function flattenSchema(rootSchema) {
    const fields = [];
    const skippedGroups = [];
    const groupFields = []; // repeatable object-array fields: Label Image, External Product Identifier, Fabric Content, Swatch Images, State Restrictions, etc.
    const conditionalRules = []; // allOf/if-then rules: { watchers: [{path, values, arrayContains}], targets: [path,...] }
    let itemContainerKey = null; // top-level array-of-items key, e.g. "MPItem" or "SupplierItem"
    let headerKey = null; // top-level feed header key, e.g. "MPItemFeedHeader" or "SupplierItemFeedHeader"
    let countryOfOriginPath = null; // full path to whatever this schema calls "country of origin"
    let countryOfOriginIsArray = false; // WFS's TradeItem.countryOfOriginAssembly is an array; MP_ITEM's is a plain string
    const skuPaths = []; // every container that independently requires its own "sku" (Orderable AND TradeItem both do, for WFS)

    // Flattens an if-node's scalar enum checks into a flat OR'd list of
    // watchers — covers every pattern actually used in Walmart's schemas
    // (single-key enum equality, anyOf of such checks, and array-contains
    // checks like powerType containing "Battery").
    const extractWatchers = (ifNode, pathParts) => {
        if (!ifNode) return [];
        if (ifNode.anyOf) return ifNode.anyOf.flatMap((b) => extractWatchers(b, pathParts));
        if (ifNode.allOf) return ifNode.allOf.flatMap((b) => extractWatchers(b, pathParts));
        const watchers = [];
        if (ifNode.properties) {
            Object.entries(ifNode.properties).forEach(([wKey, wSchema]) => {
                if (wSchema.enum) {
                    watchers.push({ path: [...pathParts, wKey], values: wSchema.enum, arrayContains: false });
                } else if (wSchema.contains?.enum) {
                    watchers.push({ path: [...pathParts, wKey], values: wSchema.contains.enum, arrayContains: true });
                } else if (
                    wSchema.contains && !wSchema.contains.properties &&
                    Array.isArray(wSchema.contains.required) && wSchema.contains.required.length > 0
                ) {
                    // "array contains an entry with key X filled" — e.g.
                    // fabricContent contains {materialName} -> sockSize required.
                    watchers.push({ path: [...pathParts, wKey], containsRequired: wSchema.contains.required });
                }
            });
        }
        return watchers;
    };

    const walk = (node, pathParts, section, ancestorRequired = true) => {
        if (!node || typeof node !== "object") return;

        if (node.type === "array" && node.items?.properties) {
            if (pathParts.length === 0) { walk(node.items, [], null, ancestorRequired); return; }

            // A repeatable group of objects (Label Image, External Product
            // Identifier, Fabric Content, Swatch Images, State Restrictions...).
            // Flatten the item schema's own scalar sub-fields one level deep so
            // the form can render a real add/remove-entry UI, fully populated
            // and validated, instead of skipping and warning.
            const itemRequired = new Set(node.items.required || []);
            const itemFields = [];
            Object.entries(node.items.properties || {}).forEach(([subKey, subSchema]) => {
                const isArrayField = subSchema.type === "array";
                itemFields.push({
                    key: subKey,
                    title: subSchema.title || subKey,
                    type: subSchema.type,
                    enumValues: subSchema.enum || (isArrayField ? subSchema.items?.enum : null) || null,
                    required: itemRequired.has(subKey),
                });
            });

            // Walmart's schema itself only lists labelImageContains as
            // required inside this group — but a label entry with a category
            // selected and no actual image is meaningless, and Walmart's real
            // validation needs the URL too. Force it required here, the same
            // way productSecondaryImageURL is special-cased elsewhere.
            if (pathParts[pathParts.length - 1] === "labelImage") {
                const urlField = itemFields.find((f) => f.key === "labelImageURL");
                if (urlField) urlField.required = true;
            }

            groupFields.push({
                path: pathParts,
                title: node.title || pathParts[pathParts.length - 1],
                section: section || pathParts[0],
                required: ancestorRequired,
                minItems: node.minItems || 0,
                itemFields,
            });
            return;
        }

        if (node.properties) {
            const required = new Set(node.required || []);
            (node.oneOf || []).forEach((branch) => (branch.required || []).forEach((k) => required.add(k)));

            // Conditional requirements (allOf/if-then) — e.g. "labelImage" only
            // becomes required when isChemical/isAerosol/isPesticide = "Yes".
            // Captured generically here so the form can hide/require these
            // dynamically instead of always showing them as plain optional.
            (node.allOf || []).forEach((rule) => {
                const watchers = extractWatchers(rule.if, pathParts);
                const usesContainsRequired = watchers.some((w) => w.containsRequired);
                const targets = (rule.then?.required || [])
                    // Safety for the new watcher type only: never gate a field the
                    // schema already requires unconditionally, or it could get hidden.
                    .filter((k) => !(usesContainsRequired && (
                        required.has(k) ||
                        node.properties?.[k]?.type === "array" ||
                        k === "productSecondaryImageURL"
                    )))
                    .map((k) => [...pathParts, k]);
                if (watchers.length > 0 && targets.length > 0) {
                    conditionalRules.push({ watchers, targets });
                }
            });

            const propKeys = Object.keys(node.properties);
            const isMeasureUnitPair = propKeys.length === 2 && propKeys.includes("measure") && propKeys.includes("unit");

            if (isMeasureUnitPair && pathParts.length > 0) {
                fields.push({
                    path: pathParts, section, key: pathParts[pathParts.length - 1],
                    isMeasureUnit: true, unitEnum: node.properties.unit.enum || null,
                    required: ancestorRequired, title: node.title || pathParts[pathParts.length - 1],
                });
                return;
            }

            Object.entries(node.properties).forEach(([key, propSchema]) => {
                if (key === "productIdentifiers") return;

                // Some feed types (WFS) require "sku" independently inside more
                // than one container (Orderable AND TradeItem both list it as
                // required) — record every such path so buildPayload can fill
                // all of them with the one SKU value, instead of only the first.
                if (key === "sku") {
                    if (required.has("sku")) skuPaths.push([...pathParts, "sku"]);
                    return;
                }

                // Every feed type calls this something slightly different
                // (MP_ITEM: country_of_origin_substantial_transformation;
                // WFS's TradeItem container: some other key) — match by pattern
                // instead of exact name, and remember exactly where it lives so
                // buildPayload can write the value there instead of a hardcoded
                // path that may not exist in this schema.
                if (/country[_\s-]?of[_\s-]?origin/i.test(key) || /country of origin/i.test(propSchema.title || "")) {
                    if (!countryOfOriginPath) {
                        countryOfOriginPath = [...pathParts, key];
                        countryOfOriginIsArray = propSchema.type === "array";
                    }
                    return;
                }

                // The feed header container (MPItemFeedHeader, SupplierItemFeedHeader,
                // etc) — detected by suffix instead of an exact name so this works
                // across every Walmart feed type. We build this ourselves in
                // buildPayload (businessUnit/locale/version never need user input).
                if (pathParts.length === 0 && key.endsWith("FeedHeader")) {
                    headerKey = key;
                    return;
                }

                // The single top-level array-of-items container (MPItem,
                // SupplierItem, etc) — detected structurally (an array of objects
                // sitting at the schema root) instead of by name, for the same reason.
                if (pathParts.length === 0 && propSchema.type === "array" && propSchema.items?.properties) {
                    itemContainerKey = key;
                    walk(propSchema.items, [], null, true);
                    return;
                }

                const newPath = [...pathParts, key];
                const isContainer = propSchema.properties || (propSchema.type === "array" && propSchema.items?.properties);
                const effectiveRequired = ancestorRequired && required.has(key);

                // Plain-string array WITH a real minItems (e.g. safetyDataSheet:
                // an array of bare URL strings, not objects) — treated as its
                // own repeatable-entry group, same UI pattern as object-array
                // groups, just with a single synthetic "value" sub-field per
                // entry. Only fires when minItems > 0, so ordinary optional
                // string-array fields (accessoriesIncluded, occasion, etc.)
                // are unaffected and keep using the plain comma-separated
                // fields.push() path below.
                // productSecondaryImageURL matches this shape (plain string array,
                // minItems > 0) exactly like safetyDataSheet does — but unlike
                // safetyDataSheet, the rest of this form has dedicated special-case
                // handling for productSecondaryImageURL specifically (image picker,
                // main/secondary sync effect, "Additional Images" section, the
                // re-add-if-filtered-out logic in fetchSchema) that all expect it
                // to live in `fields`, not `groupFields`. Routing it into
                // groupFields here made it silently invisible to all of that,
                // which is why secondary images were never reaching the payload.
                const isPlainStringArrayGroup = key !== "productSecondaryImageURL"
                    && propSchema.type === "array" && propSchema.items
                    && !propSchema.items.properties && propSchema.items.type === "string"
                    && !propSchema.items.enum && (propSchema.minItems || 0) > 0;

                if (isContainer) {
                    const nextSection = pathParts.length === 0 ? key : section;
                    walk(propSchema, newPath, nextSection, effectiveRequired);
                } else if (isPlainStringArrayGroup) {
                    groupFields.push({
                        path: newPath,
                        title: propSchema.title || key,
                        section: section || newPath[0],
                        required: effectiveRequired,
                        minItems: propSchema.minItems || 0,
                        isPlainStringArray: true,
                        itemFields: [{ key: "value", title: propSchema.title || key, type: "string", enumValues: null, required: true }],
                    });
                } else {
                    const isArrayField = propSchema.type === "array";
                    fields.push({
                        path: newPath, section: section || newPath[0], key,
                        type: propSchema.type,
                        enumValues: propSchema.enum || (isArrayField ? propSchema.items?.enum : null) || null,
                        isStringArray: isArrayField && !propSchema.items?.enum,
                        isMultiSelect: isArrayField && !!propSchema.items?.enum,
                        required: effectiveRequired,
                        minItems: isArrayField ? propSchema.minItems || null : null,
                        title: propSchema.title || key,
                    });
                }
            });
        }
    };

    walk(rootSchema, [], null, true);
    return {
        fields, skippedGroups, groupFields, conditionalRules,
        itemContainerKey: itemContainerKey || "MPItem",
        headerKey: headerKey || "MPItemFeedHeader",
        countryOfOriginPath: countryOfOriginPath || ["Orderable", "country_of_origin_substantial_transformation"],
        countryOfOriginIsArray,
        skuPaths: skuPaths.length > 0 ? skuPaths : [["Orderable", "sku"]],
    };
}

// Skipped array groups where a safe, common default is well-defined — these
// get auto-filled instead of contact-support-blocking the item. Walmart's
// stateRestrictions only strictly requires stateRestrictionsText per entry,
// and "None" is a valid, common answer meaning "no restrictions."
const SKIPPED_GROUP_DEFAULTS = {
    stateRestrictions: [{ stateRestrictionsText: "None" }],
};

const setDeep = (obj, path, value) => {
    let cur = obj;
    for (let i = 0; i < path.length - 1; i++) { cur[path[i]] = cur[path[i]] || {}; cur = cur[path[i]]; }
    cur[path[path.length - 1]] = value;
};

// Common abbreviations that occasionally slip through (AI-fill, pasted
// data) instead of the real enum value — resolved before ever reaching
// Walmart, since submitting "Y" where only "Yes"/"No" are valid gets the
// whole item rejected.
const ENUM_ALIASES = { y: "Yes", yes: "Yes", n: "No", no: "No" };
const resolveEnumValue = (enumValues, raw) => {
    if (!enumValues || enumValues.length === 0) return raw;
    const strRaw = String(raw).trim();
    if (enumValues.includes(strRaw)) return strRaw;
    const ci = enumValues.find((v) => v.toLowerCase() === strRaw.toLowerCase());
    if (ci) return ci;
    const alias = ENUM_ALIASES[strRaw.toLowerCase()];
    if (alias && enumValues.includes(alias)) return alias;
    return null; // genuinely invalid — caller should omit rather than submit it
};

// Scraped/knownValues data sometimes carries a retailer's own "content
// coming soon" filler straight through as if it were real copy — this is
// exactly what got a real listing rejected (shortDescription and every
// keyFeatures entry were literally "Feature Benefits not yet available.
// Coming Soon!"). Detected on description/feature fields specifically so a
// legitimately short but real value elsewhere (a size, a SKU) is never
// false-flagged.
const PLACEHOLDER_PATTERNS = [
    /feature\s*benefits?\s*not\s*yet\s*available/i,
    /not\s*yet\s*available/i,
    /coming\s*soon/i,
    /\blorem\s*ipsum\b/i,
    /\bplaceholder\b/i,
    /\btbd\b/i,
    /\bto\s*be\s*(determined|announced|added|updated)\b/i,
    /^(test|sample|dummy)\s*(description|content|text|data)?$/i,
    /no\s*(information|info|details|description)\s*(is\s*)?(currently\s*)?available/i,
    /(currently|presently)\s*no\s*(information|info|details)/i,
    /updates?\s*will\s*be\s*provided/i,
    /prior\s*to\s*launch/i,
    /\blaunch\s*date\b/i,
    /information\s*(will|to)\s*be\s*(provided|added|updated)/i,
    /details\s*(will\s*be|to\s*be|coming)/i,
    /check\s*back\s*(later|soon)/i,
];
const isPlaceholderText = (val) => PLACEHOLDER_PATTERNS.some((re) => re.test(String(val).trim()));

const isQualityCheckedField = (f) =>
    /description/i.test(f.key) || /description/i.test(f.title || "") ||
    f.key === "shortDescription" || f.key === "longDescription";

const isQualityCheckedGroupField = (groupKey, f) => groupKey === "keyFeatures" && f.key === "value";

// Triggers the Chrome extension's SDS-search automation (opens
// wercsmart.com, types the search term, clicks search, highlights any
// Documents links found) via the same messaging pattern used elsewhere in
// the app. Falls back to just opening the search page directly — with the
// term pre-filled as a query param where possible — if the extension isn't
// installed/available, so the button always does SOMETHING useful.
const triggerSdsSearch = (searchTerm) => {
    if (!searchTerm) return;
    // Copy the identifier to clipboard too — the auto-fill on wercsmart.com
    // doesn't always land correctly depending on how their page renders, so
    // having it already on the clipboard means the seller can just paste it
    // straight into the search box as a reliable fallback.
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(searchTerm).catch(() => { });
    }
    const fallbackUrl = `https://rpswv.wercsmart.com/Walmartstores_SDS/WV/Store?upc=${encodeURIComponent(searchTerm)}`;

    if (window.chrome?.runtime?.sendMessage && EXT_ID) {
        try {
            window.chrome.runtime.sendMessage(EXT_ID, { type: "OPEN_SDS_SEARCH", searchTerm }, () => {
                // sendMessage failures (extension not installed, no listener,
                // etc.) surface here asynchronously via chrome.runtime.lastError
                // — NOT as a thrown exception, so a plain try/catch around the
                // call above never catches this. Checking it in the callback
                // is the only reliable way to detect "message didn't land" and
                // fall back to just opening the page directly.
                if (window.chrome.runtime.lastError) {
                    window.open(fallbackUrl, "_blank");
                }
            });
            return;
        } catch {
            // sendMessage itself threw synchronously (e.g. extension APIs
            // unavailable at all) — fall through to direct fallback below.
        }
    }
    window.open(fallbackUrl, "_blank");
};

const PRODUCT_ID_TYPES = ["UPC", "GTIN", "ISBN", "EAN"];
// Walmart's expected digit length per identifier type. GTIN is the umbrella
// standard (GTIN-8/12/13/14) so it validly overlaps with UPC (GTIN-12) and
// EAN (GTIN-13) — Walmart accepts a 13-digit code labeled either "EAN" or
// "GTIN". ISBN has two valid lengths too (old 10-digit vs current 13-digit).
const PRODUCT_ID_LENGTHS = { UPC: [12], GTIN: [13, 14], ISBN: [10, 13], EAN: [13] };

// Digits only, and must match one of the valid lengths for the selected type.
// Returns null when valid, or a short message to show under the field.
const validateProductId = (idType, rawValue) => {
    const val = String(rawValue || "").trim();
    if (!val) return "Required";
    if (!/^\d+$/.test(val)) return `${idType} must contain digits only`;
    const validLengths = PRODUCT_ID_LENGTHS[idType] || [];
    if (!validLengths.includes(val.length)) {
        const expected = validLengths.join(" or ");
        return `${idType} must be ${expected} digits (got ${val.length})`;
    }
    return null;
};
const SECTION_LABELS = { Orderable: "Basic Details", Visible: "Product Details" };

// Same defaults as the extension's profit calculator (WMgoodROI / WMgoodProfit).
// Used only for the "Max cost" hint under Cost Price.
const GOOD_ROI = 20;   // %
const GOOD_PROFIT = 1; // $

// Searches every buy list the seller has for a row whose UPC column
// matches searchId, showing price + list name so a cost price can be
// pulled straight from prior buy-list data. Column names vary per list
// (same reason BatchesPage's own buy-list importer matches by substring
// rather than exact header), so we do the same loose matching here.
const BuylistPriceCheckModal = ({ searchId, onSelectPrice, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [matches, setMatches] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const listsResult = await authService.getBuyListNames('all');
                if (!listsResult.success) throw new Error("Failed to load buy lists");
                const allMatches = [];
                for (const { name, platform } of listsResult.listNames || []) {
                    const exp = await authService.exportBuyList(name, platform);
                    if (!exp.success || !exp.csvData?.length) continue;
                    const [headers, ...rows] = exp.csvData;
                    const upcIdx = headers.findIndex((h) => h.toLowerCase().includes("upc"));
                    const findCol = (...patterns) => {
                        for (const p of patterns) {
                            const idx = headers.findIndex((h) => h.toLowerCase().includes(p));
                            if (idx !== -1) return idx;
                        }
                        return -1;
                    };
                    const priceIdx = findCol("cost price", "buy cost", "cost basis", "price", "cost");
                    const dateIdx = headers.findIndex((h) => h.toLowerCase().includes("date"));
                    const nameIdx = headers.findIndex((h) => h.toLowerCase().includes("name") || h.toLowerCase().includes("title"));
                    if (upcIdx === -1) continue;
                    rows.forEach((row) => {
                        if (String(row[upcIdx] || "").trim() === String(searchId || "").trim()) {
                            allMatches.push({
                                list: name, platform,
                                title: nameIdx !== -1 ? row[nameIdx] : "",
                                price: priceIdx !== -1 ? row[priceIdx] : "",
                                date: dateIdx !== -1 ? row[dateIdx] : "",
                            });
                        }
                    });
                }
                setMatches(allMatches);
            } catch (e) {
                setError(e.message || "Failed to search buy lists");
            } finally {
                setLoading(false);
            }
        })();
    }, [searchId]);

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[80vh] flex flex-col">
                <div className="bg-gradient-to-r from-purple-500 to-teal-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <h2 className="text-white font-bold text-lg">Buy List Price Check</h2>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
                </div>
                <div className="p-5 overflow-y-auto">
                    {loading && <p className="text-sm text-gray-400 text-center py-6">Searching your buy lists for "{searchId}"...</p>}
                    {!loading && error && <p className="text-sm text-red-500 text-center py-6">{error}</p>}
                    {!loading && !error && matches.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-6">No matches found for "{searchId}" in any buy list.</p>
                    )}
                    {!loading && matches.map((m, i) => (
                        <button key={i} type="button" onClick={() => onSelectPrice(m.price)}
                            className="w-full text-left border border-gray-200 hover:border-teal-400 rounded-xl p-3 mb-2 transition">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-gray-700">{m.list}</span>
                                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full uppercase font-bold">{m.platform}</span>
                            </div>
                            {m.title && <p className="text-xs text-gray-500 truncate mt-1">{m.title}</p>}
                            <div className="flex items-center justify-between mt-1">
                                <span className="text-sm font-bold text-teal-700">{m.price ? `$${m.price}` : "No price"}</span>
                                {m.date && <span className="text-[10px] text-gray-400">{m.date}</span>}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

/* ─── Buy List auto-fill ───
   Loose header matching, same idea as BuylistPriceCheckModal. Patterns are
   tried in priority order; `exclude` stops e.g. "Total Cost" or
   "Source URL" from being mistaken for the real field. */
const BUYLIST_FIELD_MAP = [
    { key: "costPrice", type: "number", patterns: ["cost price", "buy cost", "unit cost", "cost basis", "buy price", "purchase price", "cost"], exclude: /total|max|fee|ship|profit|roi|sell|sale|break/i },
    { key: "price", type: "number", patterns: ["sell price", "sale price", "selling price", "list price", "target price"], exclude: /total|min|max/i },
    { key: "supplier", type: "text", patterns: ["supplier", "source", "store", "retailer", "vendor", "purchased from", "bought from"], exclude: /url|link|http/i },
    { key: "quantity", type: "int", patterns: ["quantity", "qty", "units"], exclude: /per|case|pack|sold|total/i },
    { key: "purchaseDate", type: "date", patterns: ["purchase date", "date purchased", "buy date", "bought on", "order date"], exclude: /exp/i },
    { key: "expirationDate", type: "date", patterns: ["expiration", "expiry", "exp date", "best by", "use by"], exclude: null },
];

// Accepts YYYY-MM-DD, MM/DD/YYYY, MM-DD-YY, or anything Date() can parse —
// returns YYYY-MM-DD for <input type="date">.
const toIsoDate = (s) => {
    let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
    m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
    if (m) {
        const y = m[3].length === 2 ? `20${m[3]}` : m[3];
        return `${y}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
    }
    const d = new Date(s);
    return isNaN(d) ? null : d.toISOString().slice(0, 10);
};

const cleanBuylistValue = (raw, type) => {
    const s = String(raw ?? "").trim();
    if (!s) return null;
    if (type === "number") {
        const n = parseFloat(s.replace(/[^0-9.\-]/g, ""));
        return isFinite(n) ? String(n) : null;
    }
    if (type === "int") {
        const n = parseInt(s.replace(/[^0-9\-]/g, ""), 10);
        return isFinite(n) && n >= 0 ? String(n) : null;
    }
    if (type === "date") return toIsoDate(s);
    return s;
};

// allFields: [{label, value}] from the buy-list row -> { costPrice, price, supplier, ... }
const extractBuylistValues = (allFields) => {
    const out = {};
    const used = new Set(); // one column can only fill one field
    BUYLIST_FIELD_MAP.forEach((spec) => {
        for (const p of spec.patterns) {
            const hit = allFields.find((f) =>
                !used.has(f.label) &&
                String(f.label).toLowerCase().includes(p) &&
                !(spec.exclude && spec.exclude.test(f.label))
            );
            if (!hit) continue;
            const v = cleanBuylistValue(hit.value, spec.type);
            if (v != null) { out[spec.key] = v; used.add(hit.label); break; }
        }
    });
    return out;
};

const findBuylistColumn = (allFields, name) =>
    allFields.find((f) => String(f.label).trim().toLowerCase() === String(name || "").trim().toLowerCase());

const BuylistBadge = () => (
    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full ml-1">
        📋 Buy List
    </span>
);

/* ─── Shared item form — used by SF-match, new-listing, bundle, multipack,
   copy-from-item-id. Callers supply productType + prefill data; this
   component only knows how to render/collect/submit a schema.

   Two prefill sources are supported, both optional and mergeable:
   - knownValues: a small map keyed by bare leaf field name (e.g. "brand"),
     used by SF-match where values come from scraping.
   - prefilledValues: a map keyed by the EXACT dotted schema path (e.g.
     "Visible.Stuffed Animals & Plush Toys.brand", or
     "...assembledProductHeight.measure" for measure/unit pairs), used by
     AI-fill (new listing), which already returns full paths.
   prefilledValues takes priority when both are present for a field. ─── */
const WalmartItemForm = ({
    productType, knownValues, prefilledValues, identifierType, identifierValue,
    imageUrl, secondaryImageUrls, itemId, batchId, onAddToBatch, onBack,
    imageEditable, onImageChange, titleEditable,
    initialItem, // optional: a raw stored batch_item row (retry) — see hydrateFromInitialItem below
    feedType = "MP_ITEM", version = MP_ITEM_VERSION, // override for WFS: "MP_WFS_ITEM" or "OMNI_WFS"
    suggestedSku, // optional: prefill from an existing matching item — editable, just a strong suggestion (Walmart won't let you change SKU on an item that already exists)
    existingPrice, // optional: THIS SELLER's own current listed price for this item (from catalog_items.price), when it already exists in their catalog — takes priority over knownValues.price (the BuyBox/catalog price) as the Sell Price prefill
    productImages = [], // full list of this product's image URLs — used by the Label Image / Swatch Images "pick from photos" popover
    specifications = [], // raw [{name, value}] spec list scraped from Walmart's own page — generic fallback fill by title match, for fields knownValues doesn't cover
    buylistFields = null, // [{label, value}] — the full buy-list row when this item came from a Buy List import; auto-fills cost/supplier/qty/dates/etc.
}) => {
    const [stage, setStage] = useState("fetching_schema"); // 'fetching_schema' | 'form'
    const [error, setError] = useState("");

    const [fields, setFields] = useState([]);
    const [skippedGroups, setSkippedGroups] = useState([]);
    const [itemContainerKey, setItemContainerKey] = useState("MPItem");
    const [headerKey, setHeaderKey] = useState("MPItemFeedHeader");
    const [groupFields, setGroupFields] = useState([]);
    const [groupValues, setGroupValues] = useState({}); // pathKey -> array of entry objects, e.g. { labelImageURL, labelImageContains }
    const [imagePicker, setImagePicker] = useState(null); // { pathKey, idx, subKey } while the picker is open
    const [uploadTarget, setUploadTarget] = useState(null); // where a just-picked file's URL should be written
    const [uploading, setUploading] = useState(false);
    const uploadInputRef = React.useRef(null);
    const [conditionalRules, setConditionalRules] = useState([]); // allOf/if-then rules from the schema — see flattenSchema
    const [countryOfOriginPath, setCountryOfOriginPath] = useState(["Orderable", "country_of_origin_substantial_transformation"]);
    const [countryOfOriginIsArray, setCountryOfOriginIsArray] = useState(false);
    const [skuPaths, setSkuPaths] = useState([["Orderable", "sku"]]);
    const [values, setValues] = useState({});

    const [sku, setSku] = useState("");
    const [skuModalOpen, setSkuModalOpen] = useState(false);
    // Starts locked whenever this item already has a real, existing SKU
    // (suggestedSku) — Walmart won't let you change the SKU of an item
    // that already exists, so the live-recompute effect below must never
    // silently overwrite it just because the seller edited price/cost/
    // supplier/etc. Only an explicit manual edit (typing in the SKU box)
    // or the seller re-generating it via SKU Builder should ever touch it.
    const [skuManuallyEdited, setSkuManuallyEdited] = useState(!!suggestedSku);
    const [skuPattern, setSkuPattern] = useState(() => {
        try { return JSON.parse(localStorage.getItem(SKU_PATTERN_KEY)) || defaultSkuPattern(); }
        catch { return defaultSkuPattern(); }
    });

    const [countryOfOrigin, setCountryOfOrigin] = useState("");
    // Editable regardless of whether identifierValue arrived prefilled (matched-item
    // flow) or empty (new-listing flow) — same pattern as sku/countryOfOrigin below.
    const [idType, setIdType] = useState(identifierType || "UPC");
    const [idValue, setIdValue] = useState(identifierValue || "");
    const [costPrice, setCostPrice] = useState("");
    const [showBuylistCheck, setShowBuylistCheck] = useState(false);
    // Auto-filled from the last item added to THIS batch (see
    // loadBatchLastValues/saveBatchLastValue) — skipped when retrying a
    // saved batch_item, since hydrateFromInitialItem below is the source
    // of truth for those and runs its own setSupplier() call.
    const [supplier, setSupplier] = useState(() => (initialItem ? "" : (loadBatchLastValues(batchId).supplier || "")));
    const [expirationDate, setExpirationDate] = useState("");
    const [purchaseDate, setPurchaseDate] = useState(() => (initialItem ? "" : (loadBatchLastValues(batchId).purchaseDate || "")));
    const [quantity, setQuantity] = useState("");

    const [costDefs, setCostDefs] = useState([]);
    const [flagDefs, setFlagDefs] = useState([]);
    const [fieldDefs, setFieldDefs] = useState([]);
    const [selectedCosts, setSelectedCosts] = useState({});
    const [selectedFlagIds, setSelectedFlagIds] = useState([]);
    const [selectedFields, setSelectedFields] = useState({});

    const [aiLoading, setAiLoading] = useState(false);
    const [aiFilledPaths, setAiFilledPaths] = useState(new Set()); // paths written by AI-fill — used to purple-highlight for review
    const [finishing, setFinishing] = useState(false);
    const [finishError, setFinishError] = useState("");
    const [showFeeSettings, setShowFeeSettings] = useState(false);
    const [feeSettingsVersion, setFeeSettingsVersion] = useState(0);
    const [showRepricer, setShowRepricer] = useState(false);
    const formRef = React.useRef(null);
    const [barRect, setBarRect] = useState(null); // {left, width} — keeps the fixed bottom bar aligned with the form's own horizontal position/width

    const [costPriceDefId, setCostPriceDefId] = useState(null);

    // Buy List auto-fill — keys filled from buylistFields, for the 📋 badge.
    const [buylistFilled, setBuylistFilled] = useState(new Set());
    const buylistAppliedRef = React.useRef(false);
    const markBuylist = (keys) => setBuylistFilled((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.add(k));
        return next;
    });

    // Core fields — once, right after the form is ready. Overrides the
    // per-batch "last used" supplier/purchase date, since the buy-list row
    // is specific to THIS item.
    useEffect(() => {
        if (stage !== "form" || buylistAppliedRef.current || !buylistFields?.length) return;
        buylistAppliedRef.current = true;
        const v = extractBuylistValues(buylistFields);
        const filled = [];
        if (v.costPrice != null) { setCostPrice(v.costPrice); filled.push("costPrice"); }
        if (v.supplier) { setSupplier(v.supplier); filled.push("supplier"); }
        if (v.quantity != null) { setQuantity(v.quantity); filled.push("quantity"); }
        if (v.purchaseDate) { setPurchaseDate(v.purchaseDate); filled.push("purchaseDate"); }
        if (v.expirationDate) { setExpirationDate(v.expirationDate); filled.push("expirationDate"); }
        // Your own live listed price (existingPrice) still wins over a buy-list sell price.
        const priceF = fields.find((f) => f.key === "price");
        if (v.price != null && priceF && existingPrice == null) {
            setValues((prev) => ({ ...prev, [priceF.path.join(".")]: v.price }));
            filled.push("price");
        }
        if (filled.length) markBuylist(filled);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage, fields, buylistFields]);

    // Custom costs / custom fields — matched by exact name to a buy-list
    // column. Runs whenever your definitions finish loading; never
    // overwrites a value you've already typed.
    useEffect(() => {
        if (!buylistFields?.length) return;
        const filled = [];
        const costUpdates = {};
        costDefs.forEach((d) => {
            if (d.id === costPriceDefId) return;
            const hit = findBuylistColumn(buylistFields, d.name);
            const val = hit ? cleanBuylistValue(hit.value, "number") : null;
            if (val != null) { costUpdates[d.id] = val; filled.push(`cost:${d.id}`); }
        });
        const fieldUpdates = {};
        fieldDefs.forEach((d) => {
            const hit = findBuylistColumn(buylistFields, d.name);
            const val = hit ? cleanBuylistValue(hit.value, "text") : null;
            if (val != null) { fieldUpdates[d.id] = val; filled.push(`field:${d.id}`); }
        });
        const fillEmpty = (prev, updates) => {
            const next = { ...prev };
            Object.entries(updates).forEach(([k, v]) => { if (next[k] == null || next[k] === "") next[k] = v; });
            return next;
        };
        if (Object.keys(costUpdates).length) setSelectedCosts((prev) => fillEmpty(prev, costUpdates));
        if (Object.keys(fieldUpdates).length) setSelectedFields((prev) => fillEmpty(prev, fieldUpdates));
        if (filled.length) markBuylist(filled);
    }, [costDefs, fieldDefs, buylistFields, costPriceDefId]);

    useEffect(() => {
        listCostDefinitions().then(async (d) => {
            const defs = d.definitions || [];
            let cp = defs.find((c) => c.key === "cost_price" || c.name.toLowerCase() === "cost price");
            if (!cp) {
                const created = await createCostDefinition("Cost Price").catch(() => null);
                cp = created?.definition;
                if (cp) defs.push(cp);
            }
            setCostDefs(defs);
            if (cp) setCostPriceDefId(cp.id);
        }).catch(() => { });
        listFlagDefinitions().then((d) => setFlagDefs(d.definitions || [])).catch(() => { });
        listFieldDefinitions().then((d) => setFieldDefs(d.definitions || [])).catch(() => { });
    }, []);

    useEffect(() => {
        fetchSchema();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Live-regenerates the SKU as price/cost/supplier/custom costs-flags-
    // fields change elsewhere in the form — stops the moment the user types
    // into the SKU field themselves (skuManuallyEdited), so it never fights
    // their own edits. Also skipped entirely for a suggested/existing SKU.
    useEffect(() => {
        if (stage !== "form" || skuManuallyEdited) return;
        setSku(buildSku(skuPattern, buildSkuCtx()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, costPrice, supplier, expirationDate, idValue, skuPattern, selectedCosts, selectedFields, selectedFlagIds, stage]);

    // Keeps the fixed bottom action bar aligned with the form card's actual
    // width/position, since position:fixed anchors to the viewport, not this
    // component's own layout — recalculated on resize and whenever the
    // form's content changes size (which can shift its position if it's
    // inside a flexible layout).
    useEffect(() => {
        const measure = () => {
            if (!formRef.current) return;
            const rect = formRef.current.getBoundingClientRect();
            setBarRect({ left: rect.left * 0.93, width: rect.width * 0.885 });
        };
        measure();
        window.addEventListener("resize", measure);
        const interval = setInterval(measure, 500); // cheap safety net for layout shifts not caught by resize
        return () => {
            window.removeEventListener("resize", measure);
            clearInterval(interval);
        };
    }, [stage]);

    // Extra function only: fills every editable field on this form from a
    // previously-saved batch_item row. Called once, right after fields/values
    // are set up in fetchSchema below — nothing else in the file is changed.
    const hydrateFromInitialItem = (flatFields, baseValues, countryPath, countryIsArray) => {
        if (!initialItem) return baseValues;

        const payload = initialItem.payload || {};
        setSku(initialItem.sku || "");
        setQuantity(initialItem.quantity != null ? String(initialItem.quantity) : "");
        setSupplier(initialItem.supplier || "");
        setExpirationDate(initialItem.expiration_date || "");

        const rawCountry = countryPath.reduce((n, k) => (n == null ? undefined : n[k]), payload);
        if (countryIsArray && Array.isArray(rawCountry)) {
            // Stored as ["US - United States"] — strip the code prefix back
            // off so the picker shows/searches the plain country name.
            setCountryOfOrigin(String(rawCountry[0] || "").replace(/^[A-Z]{2}\s*-\s*/, ""));
        } else {
            setCountryOfOrigin(rawCountry || "");
        }
        if (payload.Orderable?.productIdentifiers) {
            setIdType(payload.Orderable.productIdentifiers.productIdType || "UPC");
            setIdValue(payload.Orderable.productIdentifiers.productId || "");
        }

        const initCosts = {};
        (initialItem.costs || []).forEach((c) => { initCosts[c.cost_definition_id] = String(c.value); });
        setSelectedCosts(initCosts);
        const cpCost = (initialItem.costs || []).find((c) => c.name?.toLowerCase() === "cost price");
        if (cpCost) setCostPrice(String(cpCost.value));
        setSelectedFlagIds((initialItem.flags || []).map((f) => f.flag_definition_id));

        const initFields = {};
        (initialItem.fields || []).forEach((f) => { initFields[f.field_definition_id] = f.value; });
        setSelectedFields(initFields);

        // Walk the stored payload and pull a value for every field this form
        // knows about, using each field's own path — same lookup shape the
        // rest of the form already uses (flat dotted key -> value).
        const getAtPath = (path) => path.reduce((n, k) => (n == null ? undefined : n[k]), payload);
        const next = { ...baseValues };
        flatFields.forEach((f) => {
            if (f.isMeasureUnit) {
                const node = getAtPath(f.path);
                if (node?.measure != null) next[`${f.path.join(".")}.measure`] = String(node.measure);
                if (node?.unit) next[`${f.path.join(".")}.unit`] = node.unit;
            } else {
                const val = getAtPath(f.path);
                if (val !== undefined) next[f.path.join(".")] = Array.isArray(val) ? val.join(",") : val;
            }
        });
        return next;
    };

    // Best-effort defaults for repeatable groups, using data we already have —
    // never left blank when we can fill it correctly, never guessed when we can't.
    const buildAutoGroupEntries = (groupKey) => {
        if (groupKey === "stateRestrictions") {
            // "None" is a valid, well-defined answer meaning "no restrictions" —
            // the common case for the vast majority of items.
            return [{ stateRestrictionsText: "None" }];
        }
        if (groupKey === "externalProductIdentifier") {
            const asin = knownValues?.asin;
            return asin ? [{ externalProductIdType: "ASIN", externalProductId: asin }] : [];
        }
        if (groupKey === "fabricContent") {
            const material = knownValues?.material;
            if (!material) return [];
            const first = String(material).split(",")[0].trim();
            return first ? [{ materialName: first }] : [];
        }
        if (groupKey === "keyFeatures") {
            // "Key Features" is a repeatable, minItems-required GROUP field on
            // this product type (not a scalar) — real, scraped bullet text
            // already exists (knownValues.keyFeaturesList), so use it directly
            // instead of leaving it for the seller/AI to fill by hand.
            const list = knownValues?.keyFeaturesList;
            return Array.isArray(list) && list.length > 0 ? list.map((v) => ({ value: v })) : [];
        }
        // labelImage, swatchImages: no safe auto value — these need an actual
        // product photo the seller picks, so they start empty. The seller adds
        // an entry manually (via "+ Add Entry" and the "Pick Image" popover)
        // only if this item actually needs one.
        return [];
    };

    // Builds the current SKU-pattern context from live form state, so the
    // same context object is used for initial fill, live updates, and the
    // SKU Builder modal's preview.
    const buildSkuCtx = () => {
        const priceFieldPath = fields.find((f) => f.key === "price")?.path.join(".");
        const livePrice = priceFieldPath ? values[priceFieldPath] : knownValues?.price;
        return {
            item_id: itemId, upc: idValue, gtin: idValue,
            title: knownValues?.productName, brand: knownValues?.brand,
            sell_price: livePrice, cost_price: costPrice, source: supplier,
            breakeven: profitEstimate?.breakeven ?? null,
            max_cost: profitEstimate?.maxCost ?? null,
            expiration_date: expirationDate,
            costs: selectedCosts, costDefs,
            customFields: selectedFields, fieldDefs,
            flags: selectedFlagIds, flagDefs,
        };
    };

    const fetchSchema = async () => {
        setStage("fetching_schema");
        setError("");
        try {
            if (!productType) throw new Error("No product type provided — can't fetch a schema.");
            const resp = await api("/api/walmart/api/item-schema", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ feed_type: feedType, version: version, product_types: [productType] }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.detail || data.error || "Failed to fetch item schema");

            const schemaRoot = data.schema || data;
            const { fields: allFields, skippedGroups: skipped, groupFields: detectedGroupFields, conditionalRules: detectedConditionalRules, itemContainerKey: detectedItemKey, headerKey: detectedHeaderKey, countryOfOriginPath: detectedCountryPath, countryOfOriginIsArray: detectedCountryIsArray, skuPaths: detectedSkuPaths } = flattenSchema(schemaRoot);

            // Fields that are ONLY conditionally required (e.g.
            // frontFacingCameraMegapixels, required solely because of an
            // allOf rule tied to hasFrontFacingCamera = "Yes") compute as
            // required: false here, since flattenSchema only marks a field
            // required if it's in ITS OWN container's static required[]
            // list. The plain `f.required` filter below would drop such
            // fields before gating ever gets a chance to reveal them — this
            // is what silently broke Front/Rear Camera Megapixels (and
            // would equally affect prop65WarningText, warrantyText/URL,
            // certification doc IDs, restoredProductIdentifier, etc. on
            // other categories). Also covers every leaf under a
            // conditionally-required CONTAINER (e.g. lithiumIonBatteries'
            // children), via prefix matching against each rule's target path.
            const startsWithPathLocal = (fieldPath, prefixPath) =>
                prefixPath.length <= fieldPath.length && prefixPath.every((seg, i) => fieldPath[i] === seg);
            const isConditionallyTargeted = (fieldPath) =>
                detectedConditionalRules.some((rule) => rule.targets.some((t) => startsWithPathLocal(fieldPath, t)));

            const flat = allFields.filter((f) => f.required || isConditionallyTargeted(f.path));
            if (flat.length === 0) throw new Error("Schema returned no required fields for this product type.");

            // productSecondaryImageURL is optional in Walmart's schema (only
            // mainImageUrl is required), so the required-only filter above drops
            // it — meaning multiple photos silently never reach values/buildPayload.
            // Force it back in so its value is tracked, but leave required FALSE:
            // 0 secondary photos is valid; the real Walmart rule (0 fine, 1-2
            // invalid, 3+ fine) is judged separately by photosMissingCount below,
            // not by the generic required-field check.
            const secondaryImageField = allFields.find((f) => f.key === "productSecondaryImageURL");
            if (secondaryImageField) {
                // Remember whether THIS product type's schema genuinely
                // requires it (varies by category — this SKU's rejection
                // confirmed some categories do) before we relax the flag for
                // the generic 0-fine/1-2-invalid/3+-fine rule below.
                secondaryImageField.actuallyRequired = secondaryImageField.required;
                secondaryImageField.required = false;
                secondaryImageField.minItems = secondaryImageField.minItems || 3;
                if (!flat.some((f) => f.key === "productSecondaryImageURL")) flat.push(secondaryImageField);
            }

            // longDescription is optional in the schema, so the required-only
            // filter drops it, but Walmart's content review still judges it.
            const longDescField = allFields.find((f) => f.key === "longDescription");
            if (longDescField && !flat.includes(longDescField)) flat.push(longDescField);

            // Generic fallback source: match a required field's TITLE against
            // Walmart's own raw spec list (e.g. schema title "Animal Type" vs
            // spec name "Animal type"). Catches category-specific fields
            // knownValues was never hardcoded for, without per-category code.
            const specMap = {};
            (specifications || []).forEach((s) => {
                if (s?.name && s?.value != null) specMap[String(s.name).trim().toLowerCase()] = String(s.value);
            });

            const initialValues = {};
            flat.forEach((f) => {
                const pathKey = f.path.join(".");
                if (f.isMeasureUnit) {
                    const directMeasure = prefilledValues?.[`${pathKey}.measure`];
                    const directUnit = prefilledValues?.[`${pathKey}.unit`];
                    const guess = knownValues?.[f.key];
                    initialValues[`${pathKey}.measure`] = directMeasure != null ? String(directMeasure) : (guess?.measure != null ? String(guess.measure) : "");
                    initialValues[`${pathKey}.unit`] = directUnit || guess?.unit || "";
                } else {
                    let val = prefilledValues?.[pathKey];
                    // Sell Price is special: if this item already exists in
                    // OUR OWN catalog, existingPrice (the seller's real
                    // current listed price) must win over knownValues.price
                    // (which is the BuyBox/catalog price scraped from
                    // Walmart's page — a different seller's price, or the
                    // page's generic price, not necessarily this seller's own).
                    if (val === undefined && f.key === "price" && existingPrice != null) {
                        val = existingPrice;
                    }
                    if (val === undefined) val = knownValues?.[f.key];
                    if (val === undefined || val === "") {
                        const specVal = specMap[String(f.title || "").trim().toLowerCase()];
                        if (specVal) val = specVal;
                    }
                    if (Array.isArray(val)) val = val.join(",");
                    initialValues[pathKey] = val ?? "";
                }
            });

            setFields(flat);
            setSkippedGroups(skipped);
            setItemContainerKey(detectedItemKey);
            setHeaderKey(detectedHeaderKey);
            setCountryOfOriginPath(detectedCountryPath);
            setCountryOfOriginIsArray(detectedCountryIsArray);
            setSkuPaths(detectedSkuPaths);
            setValues(hydrateFromInitialItem(flat, initialValues, detectedCountryPath, detectedCountryIsArray));

            const initialGroupValues = {};
            detectedGroupFields.forEach((g) => {
                const pathKey = g.path.join(".");
                if (initialItem) {
                    const raw = g.path.reduce((n, k) => (n == null ? undefined : n[k]), initialItem.payload || {});
                    if (g.isPlainStringArray) {
                        initialGroupValues[pathKey] = Array.isArray(raw) ? raw.map((v) => ({ value: v })) : [];
                    } else {
                        initialGroupValues[pathKey] = Array.isArray(raw) ? raw : [];
                    }
                } else {
                    initialGroupValues[pathKey] = buildAutoGroupEntries(g.path[g.path.length - 1]);
                }
            });
            setGroupFields(detectedGroupFields);
            setGroupValues(initialGroupValues);
            setConditionalRules(detectedConditionalRules);

            if (suggestedSku) {
                setSku(suggestedSku);
            } else if (initialItem?.sku) {
                // Retrying an existing batch_item — hydrateFromInitialItem
                // (above) already set the real original SKU; don't
                // regenerate over it. Also lock out live regeneration the
                // same way suggestedSku does, so editing price/cost/supplier
                // afterward can't silently replace it either.
                setSkuManuallyEdited(true);
            } else {
                setSku(buildSku(skuPattern, buildSkuCtx()));
            }

            setStage("form");
        } catch (err) {
            setError(err.message || "Failed to fetch item schema.");
        }
    };

    // WalmartItemForm's prefill only runs once, in fetchSchema's mount effect —
    // but imageUrl/secondaryImageUrls can change afterward (Photo Studio is
    // reachable from inside the already-open form via the "Edit Photos" badge).
    // This keeps the underlying mainImageUrl/productSecondaryImageURL schema
    // fields in sync with whatever the user picks as main or adds, any time.
    useEffect(() => {
        if (stage !== "form" || fields.length === 0) return;
        const mainField = fields.find((f) => f.key === "mainImageUrl");
        if (mainField && imageUrl) {
            const path = mainField.path.join(".");
            setValues((v) => (v[path] === imageUrl ? v : { ...v, [path]: imageUrl }));
        }
        const secondaryField = fields.find((f) => f.key === "productSecondaryImageURL");
        if (secondaryField) {
            const joined = (secondaryImageUrls || []).join(",");
            const path = secondaryField.path.join(".");
            setValues((v) => (v[path] === joined ? v : { ...v, [path]: joined }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrl, secondaryImageUrls, stage, fields]);

    // Walmart's rule: 0 photos fine, 1-2 invalid, 3+ fine. Instead of
    // blocking the seller for being short 1-2 photos, pad automatically —
    // reuse the main photo first (if not already listed), then duplicate
    // the last photo already there. The seller can still remove any
    // auto-added copy.
    useEffect(() => {
        if (stage !== "form") return;
        const secondaryField = fields.find((f) => f.key === "productSecondaryImageURL");
        if (!secondaryField?.minItems) return;
        const pathKey = secondaryField.path.join(".");
        const current = (values[pathKey] || "").split(",").filter(Boolean);
        if (current.length === 0 || current.length >= secondaryField.minItems) return;
        const padded = [...current];
        if (imageUrl && !padded.includes(imageUrl)) padded.push(imageUrl);
        while (padded.length < secondaryField.minItems) {
            padded.push(padded[padded.length - 1] || imageUrl);
        }
        if (padded.length !== current.length) {
            setValues((v) => ({ ...v, [pathKey]: padded.join(",") }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, fields, imageUrl, stage]);

    const photosMissingCount = useMemo(() => {
        let count = imageEditable && !imageUrl ? 1 : 0; // main photo required whenever user-editable (new listings)
        const secondaryField = fields.find((f) => f.key === "productSecondaryImageURL");
        if (secondaryField?.minItems) {
            const currentCount = (values[secondaryField.path.join(".")] || "").split(",").filter(Boolean).length;
            if (secondaryField.actuallyRequired) {
                // This product type genuinely requires it — 0 photos is
                // invalid too, not just the 1-2 "in between" zone.
                if (currentCount < secondaryField.minItems) count += (secondaryField.minItems - currentCount);
            } else if (currentCount > 0 && currentCount < secondaryField.minItems) {
                // Optional overall — 0 secondary photos is fine; the
                // 0-fine/1-2-invalid/3+-fine rule only kicks in once you've
                // started adding some.
                count += (secondaryField.minItems - currentCount);
            }
        }
        return count;
    }, [fields, values, imageEditable, imageUrl]);

    // ── Conditional field gating ──
    // A field/group is "gated" if it's the target of some conditional rule.
    // Gated + condition not met -> hidden entirely, never counted, never
    // submitted. Gated + condition met -> shown and treated as required,
    // regardless of the schema's own static required flag for that field.
    // Prefix matching means a rule targeting a container (e.g.
    // "lithiumIonBatteries") automatically gates every one of its child
    // fields too, without needing special-casing per field.
    const startsWithPath = (fieldPath, prefixPath) =>
        prefixPath.length <= fieldPath.length && prefixPath.every((seg, i) => fieldPath[i] === seg);

    // Accepts an optional valuesOverride so callers that track their own
    // in-progress values locally (like handleFillWithAI's retry loop) can
    // check gating against those live values instead of stale component
    // state — React state updates don't apply mid-function, so without
    // this, a field unlocked by THIS SAME AI-fill call wouldn't be seen as
    // unlocked until some future render/call.
    const isConditionActive = (rule, valuesOverride, groupValuesOverride) => rule.watchers.some((w) => {
        if (w.containsRequired) {
            const entries = (groupValuesOverride || groupValues)[w.path.join(".")] || [];
            return entries.some((e) => w.containsRequired.every((k) => String(e?.[k] ?? "").trim() !== ""));
        }
        const val = (valuesOverride || values)[w.path.join(".")];
        if (w.arrayContains) {
            return String(val || "").split(",").map((s) => s.trim()).some((v) => w.values.includes(v));
        }
        return w.values.includes(val);
    });

    const gatingInfo = (fieldPath, valuesOverride, groupValuesOverride) => {
        const matches = conditionalRules.filter((rule) => rule.targets.some((t) => startsWithPath(fieldPath, t)));
        if (matches.length === 0) return { gated: false, active: true };
        return { gated: true, active: matches.some((r) => isConditionActive(r, valuesOverride, groupValuesOverride)) };
    };

    // Auto-add empty entries the moment a repeatable group becomes required
    // with entries still short of minItems — otherwise the missing-count
    // correctly flags it (e.g. "1 group entry field needed") but there's no
    // visible row for the user to actually fill in. Fires whenever gating
    // conditions change (e.g. flipping Chemical/Aerosol/Pesticide to "Yes"
    // suddenly requires Label Image) or on initial load for statically
    // required groups.
    useEffect(() => {
        if (groupFields.length === 0) return;
        setGroupValues((prev) => {
            let changed = false;
            const next = { ...prev };
            groupFields.forEach((g) => {
                const { gated, active } = gatingInfo(g.path);
                const effectiveRequired = gated ? active : g.required;
                if (!effectiveRequired || !(g.minItems > 0)) return;
                const pathKey = g.path.join(".");
                const entries = next[pathKey] || [];
                if (entries.length < g.minItems) {
                    changed = true;
                    const toAdd = g.minItems - entries.length;
                    next[pathKey] = [...entries, ...Array.from({ length: toAdd }, () => ({}))];
                }
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupFields, conditionalRules, values]);

    const requiredMissingCount = useMemo(() => {
        let count = 0;
        fields.forEach((f) => {
            // Photo fields are judged exclusively by photosMissingCount (which
            // knows the real 0-fine/1-2-invalid/3+-fine rule) — skip them here
            // so they're never double-counted or wrongly flagged as a plain
            // required-and-empty field.
            if (f.key === "mainImageUrl" || f.key === "productSecondaryImageURL") return;

            const { gated, active } = gatingInfo(f.path);
            if (gated && !active) return; // condition not met — hidden, not counted at all
            const effectiveRequired = gated ? true : f.required;

            if (f.isMeasureUnit) {
                if (!values[`${f.path.join(".")}.measure`]) count++;
                if (!values[`${f.path.join(".")}.unit`]) count++;
            } else {
                // Use ?? not || — a legitimate numeric 0 (e.g. "Number of
                // Unique Hazardous Components") is falsy in JS, so `0 || ""`
                // would wrongly collapse it to empty and mark it missing.
                const val = String(values[f.path.join(".")] ?? "").trim();
                // For closed-list fields, a value that isn't actually one of
                // the valid options is just as incomplete as an empty one —
                // it renders as a blank <select> and gets silently dropped by
                // buildPayload's enum validation, so it must count as missing
                // here too, or the red-flag/button-disable never fires.
                const invalidEnum = f.enumValues && !f.isMultiSelect && val && !f.enumValues.includes(val);
                // Placeholder copy on a description/feature field blocks
                // submission regardless of the schema's own required flag —
                // Walmart rejects it either way.
                const isPlaceholder = isQualityCheckedField(f) && val && isPlaceholderText(val);
                if ((effectiveRequired && (!val || invalidEnum)) || isPlaceholder) count++;
            }
        });
        if (!sku.trim()) count++;
        if (!countryOfOrigin.trim()) count++;
        if (validateProductId(idType, idValue)) count++;
        return count;
    }, [fields, values, groupValues, sku, countryOfOrigin, idValue, idType, conditionalRules]);

    // Only array groups Walmart requires to have at least one entry are real
    // blockers — everything else in skippedGroups can be safely submitted as
    // an empty array (see buildPayload) without contacting support.
    // Any array field with minItems > 0 is unsafe to submit empty — whether or
    // not our (unreliable, since it can't evaluate allOf/if-then conditionals)
    // "required" computation flags it. Real Walmart validation rejected
    // Label Image / External Product Identifier / Fabric Content / Swatch
    // Images this way even though they computed as required:false — so
    // minItems alone, not the required flag, is what decides safety here.
    // Only truly required groups (per the schema's own required[] list) with
    // minItems > 0 are real blockers. Optional groups with minItems (like
    // Label Image, External Product Identifier, Fabric Content, Swatch
    // Images) are safe to simply omit — Walmart only rejects them if
    // present-but-empty, never if absent.
    // Only array groups Walmart requires to have at least one entry are real
    // blockers — everything else in skippedGroups can be safely submitted as
    // an empty array (see buildPayload) without contacting support. In
    // practice, every object-array field now becomes a real, editable
    // groupField (see below), so this stays an empty safety net.
    const blockingSkippedGroups = skippedGroups.filter((g) => g.required && g.minItems > 0 && !SKIPPED_GROUP_DEFAULTS[g.path[g.path.length - 1]]);

    // Missing entries in repeatable groups: not enough entries to satisfy a
    // required minItems, or a required sub-field left blank on an entry the
    // seller did add (whether the group itself is required or not — once you
    // add an optional entry, its own required sub-fields still must be filled
    // or Walmart will reject the submission).
    // labelImageURL is only truly required per-entry when that entry's own
    // labelImageContains isn't "No Label" — an entry marked "No Label" has
    // nothing to photograph, so forcing a URL there doesn't make sense.
    const isUrlRequiredForEntry = (groupKey, entry) => {
        if (groupKey !== "labelImage") return true;
        return entry.labelImageContains !== "No Label";
    };

    const groupMissingCount = useMemo(() => {
        let count = 0;
        groupFields.forEach((g) => {
            const { gated, active } = gatingInfo(g.path);
            if (gated && !active) return; // condition not met — hidden, no entries expected
            const effectiveRequired = gated ? true : g.required;

            const pathKey = g.path.join(".");
            const entries = groupValues[pathKey] || [];
            if (effectiveRequired && g.minItems > 0 && entries.length < g.minItems) {
                count += (g.minItems - entries.length);
            }
            const groupKey = g.path[g.path.length - 1];
            entries.forEach((entry) => {
                g.itemFields.forEach((f) => {
                    if (f.key === "labelImageURL" && !isUrlRequiredForEntry(groupKey, entry)) return;
                    const val = String(entry[f.key] ?? "").trim();
                    const invalidEnum = f.enumValues && val && !f.enumValues.includes(val);
                    const isPlaceholder = isQualityCheckedGroupField(groupKey, f) && val && isPlaceholderText(val);
                    if ((f.required && (!val || invalidEnum)) || isPlaceholder) count++;
                });
            });
        });
        return count;
    }, [groupFields, groupValues, conditionalRules, values]);

    const contentChecks = useListingContentChecks({ fields, values, setValues, groupFields, groupValues, setGroupValues });

    const totalMissingCount = requiredMissingCount + photosMissingCount + blockingSkippedGroups.length + groupMissingCount + contentChecks.issueCount;

    // Names of everything still missing — same rules as the counts above,
    // just collecting labels so the banner can say WHAT is missing.
    const missingList = (() => {
        const list = [];
        const add = (label, id) => list.push({ label, id });
        if (validateProductId(idType, idValue)) add("Product ID", "missing-top");
        if (!sku.trim()) add("SKU", "missing-top");
        if (!countryOfOrigin.trim()) add("Country of Origin", "missing-top");

        fields.forEach((f) => {
            if (f.key === "mainImageUrl" || f.key === "productSecondaryImageURL") return;
            const { gated, active } = gatingInfo(f.path);
            if (gated && !active) return;
            const effectiveRequired = gated ? true : f.required;
            const pathKey = f.path.join(".");
            const label = f.key === "price" ? "Sell Price" : f.title;
            const id = f.key === "price" ? "missing-yourdata" : `missing-${pathKey}`;

            if (f.isMeasureUnit) {
                if (!values[`${pathKey}.measure`]) add(`${f.title} — Measure`, id);
                if (!values[`${pathKey}.unit`]) add(`${f.title} — Unit`, id);
                return;
            }
            const val = String(values[pathKey] ?? "").trim();
            const invalidEnum = f.enumValues && !f.isMultiSelect && val && !f.enumValues.includes(val);
            const isPlaceholder = isQualityCheckedField(f) && val && isPlaceholderText(val);
            if (isPlaceholder) add(`${label} (placeholder text — rewrite)`, id);
            else if (effectiveRequired && (!val || invalidEnum)) add(label, id);
        });

        if (photosMissingCount > 0) {
            add(`${photosMissingCount} more photo${photosMissingCount === 1 ? "" : "s"}`, "missing-photos");
        }

        groupFields.forEach((g) => {
            const { gated, active } = gatingInfo(g.path);
            if (gated && !active) return;
            const effectiveRequired = gated ? true : g.required;
            const pathKey = g.path.join(".");
            const id = `missing-${pathKey}`;
            const groupKey = g.path[g.path.length - 1];
            const entries = groupValues[pathKey] || [];
            if (effectiveRequired && g.minItems > 0 && entries.length < g.minItems) {
                add(`${g.title} (add ${g.minItems - entries.length} more)`, id);
            }
            entries.forEach((entry, idx) => {
                g.itemFields.forEach((f) => {
                    if (f.key === "labelImageURL" && !isUrlRequiredForEntry(groupKey, entry)) return;
                    const val = String(entry[f.key] ?? "").trim();
                    const invalidEnum = f.enumValues && val && !f.enumValues.includes(val);
                    const isPlaceholder = isQualityCheckedGroupField(groupKey, f) && val && isPlaceholderText(val);
                    if ((f.required && (!val || invalidEnum)) || isPlaceholder) {
                        add(`${g.title} #${idx + 1}: ${f.title}${isPlaceholder ? " (placeholder text)" : ""}`, id);
                    }
                });
            });
        });

        list.push(...contentChecks.missingItems);

        return list;
    })();

    // Scrolls to a missing field and flashes a red ring around it for a moment.
    const scrollToMissing = (id) => {
        const el = document.getElementById(id) || formRef.current;
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-4", "ring-red-400", "rounded-lg");
        setTimeout(() => el.classList.remove("ring-4", "ring-red-400", "rounded-lg"), 1800);
    };

    const addGroupEntry = (pathKey) => setGroupValues((v) => ({ ...v, [pathKey]: [...(v[pathKey] || []), {}] }));
    const removeGroupEntry = (pathKey, idx) => setGroupValues((v) => ({ ...v, [pathKey]: (v[pathKey] || []).filter((_, i) => i !== idx) }));
    const setGroupEntryField = (pathKey, idx, subKey, val) => setGroupValues((v) => {
        const arr = [...(v[pathKey] || [])];
        arr[idx] = { ...arr[idx], [subKey]: val };
        return { ...v, [pathKey]: arr };
    });

    const setFieldValue = (path, val) => setValues((v) => ({ ...v, [path.join(".")]: val }));

    const openUpload = (target) => {
        setUploadTarget(target);
        uploadInputRef.current?.click();
    };

    const handleFileSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-picking the same file later
        if (!file || !uploadTarget) return;
        setUploading(true);
        try {
            const url = await uploadWalmartImage(file);
            if (uploadTarget.kind === "scalar") {
                setFieldValue(uploadTarget.path, url);
            } else if (uploadTarget.kind === "group") {
                setGroupEntryField(uploadTarget.pathKey, uploadTarget.idx, uploadTarget.subKey, url);
            } else if (uploadTarget.kind === "secondary-add") {
                setValues((v) => {
                    const existing = (v[uploadTarget.pathKey] || "").split(",").filter(Boolean);
                    if (existing.includes(url)) return v;
                    return { ...v, [uploadTarget.pathKey]: [...existing, url].join(",") };
                });
            }
        } catch (err) {
            setError(err.message || "Image upload failed.");
        } finally {
            setUploading(false);
            setUploadTarget(null);
        }
    };

    // Persist supplier/purchase date as "last used for this batch" any time
    // they're edited, so the NEXT item added to this same batch auto-fills
    // from whatever was just entered here.
    const handleSupplierChange = (val) => {
        setSupplier(val);
        saveBatchLastValue(batchId, "supplier", val);
    };
    const handlePurchaseDateChange = (val) => {
        setPurchaseDate(val);
        saveBatchLastValue(batchId, "purchaseDate", val);
    };

    const handleSkuSave = (pattern) => {
        setSkuPattern(pattern);
        localStorage.setItem(SKU_PATTERN_KEY, JSON.stringify(pattern));
        // Only apply the new pattern to the SKU field right now if the seller
        // hasn't manually typed a SKU — Save Pattern is meant to change how
        // FUTURE auto-generated SKUs look, not to silently discard a SKU the
        // seller already edited by hand. If they've manually edited, the
        // pattern is saved for next time but this item's SKU is left alone.
        if (!skuManuallyEdited) {
            setSku(buildSku(pattern, buildSkuCtx()));
        }
    };

    // Synthetic path format for group-entry fields sent to/from AI-fill:
    // "<groupPathKey>[<entryIndex>].<subKey>" — bracket+index makes these
    // unambiguous from plain dotted scalar paths, which never contain "[".
    const GROUP_AI_PATH_RE = /^(.+)\[(\d+)\]\.(.+)$/;

    const computeMissingForAI = (currentValues, currentGroupValues, currentCountry) => {
        const missing = [];
        if (!String(currentCountry || "").trim()) {
            missing.push({ path: "__countryOfOrigin__", title: "Country of Origin", type: "string", enum_values: COUNTRIES });
        }
        fields.forEach((f) => {
            const { gated, active } = gatingInfo(f.path, currentValues, currentGroupValues);
            if (gated && !active) return; // hidden — nothing to ask AI for
            const effectiveRequired = gated ? true : f.required;

            const pathKey = f.path.join(".");
            if (f.isMeasureUnit) {
                const measureEmpty = !currentValues[`${pathKey}.measure`];
                const unitEmpty = !currentValues[`${pathKey}.unit`];
                if (effectiveRequired && (measureEmpty || unitEmpty)) {
                    if (measureEmpty) missing.push({ path: `${pathKey}.measure`, title: `${f.title} (numeric measure)`, type: "number" });
                    if (unitEmpty) missing.push({ path: `${pathKey}.unit`, title: `${f.title} (unit)`, type: "string", enum_values: f.unitEnum });
                }
                return;
            }
            // Image fields (mainImageUrl/productSecondaryImageURL are already
            // excluded from `fields` entirely elsewhere, but category-specific
            // ones like ingredientListImage land right here) are never
            // AI-fillable — there's no legitimate source for the AI to invent
            // a real image link from. The seller must pick/paste one manually.
            const isImageField = !f.enumValues && (/image/i.test(f.key) || /image/i.test(f.title || ""));
            if (isImageField) return;
            const valForCheck = String(currentValues[pathKey] ?? "").trim();
            // Same invalid-enum check as requiredMissingCount/render —
            // a stale/bad prefilled value (e.g. from the generic specs
            // title-match fallback) that isn't one of the field's real
            // options must be treated as missing too, or AI-fill thinks
            // it's already answered and never asks for it.
            const invalidEnum = f.enumValues && !f.isMultiSelect && valForCheck && !f.enumValues.includes(valForCheck);
            const isPlaceholder = isQualityCheckedField(f) && valForCheck && isPlaceholderText(valForCheck);
            if ((effectiveRequired && (!valForCheck || invalidEnum)) || isPlaceholder) {
                missing.push({
                    path: pathKey,
                    title: isPlaceholder
                        ? `${f.title} (current value is placeholder/filler text — write real, specific product content, do not reuse it)`
                        : f.title,
                    type: f.type, enum_values: f.enumValues,
                });
            }
        });

        // Required sub-fields on repeatable-group entries the seller already
        // added (e.g. Fabric Content's "Fabric Material Percentage") — these
        // were previously invisible to AI-fill entirely, so a group entry
        // could sit half-filled forever with the button reporting "nothing
        // left to fill" while the submit button correctly blocked on it.
        groupFields.forEach((g) => {
            const { gated, active } = gatingInfo(g.path, currentValues, currentGroupValues);
            if (gated && !active) return;
            const groupKey = g.path[g.path.length - 1];
            const pathKey = g.path.join(".");
            const entries = currentGroupValues[pathKey] || [];
            entries.forEach((entry, idx) => {
                // Only ask about fields still genuinely empty on THIS entry —
                // already-filled siblings (e.g. materialName from
                // buildAutoGroupEntries) are excluded so the AI is never even
                // shown a confusing pair where one's already answered.
                // Image URL fields are never AI-fillable — there's no
                // legitimate source for the AI to invent a real image link
                // from, so asking wastes a retry pass every time. The seller
                // must pick/paste one manually (see "Pick Image" button).
                // labelImageURL is also skipped entirely when this entry's
                // labelImageContains is "No Label" — nothing to photograph.
                const emptyFields = g.itemFields.filter((f) => {
                    if (f.key === "labelImageURL" && !isUrlRequiredForEntry(groupKey, entry)) return false;
                    const isImageField = !f.enumValues && (/image/i.test(f.key) || /image/i.test(f.title || ""));
                    if (isImageField) return false;
                    const val = String(entry[f.key] ?? "").trim();
                    const invalidEnum = f.enumValues && val && !f.enumValues.includes(val);
                    const isPlaceholder = isQualityCheckedGroupField(groupKey, f) && val && isPlaceholderText(val);
                    if (!f.required && !isPlaceholder) return false;
                    return !val || invalidEnum || isPlaceholder;
                });
                emptyFields.forEach((f) => {
                    // Distinct, self-describing title per field (includes its
                    // own type/unit hint) so two similarly-named sibling
                    // fields on the same entry are never visually confusable
                    // to the model — this is what previously let a percentage
                    // number get written into the Material Name field.
                    const typeHint = f.type === "number" || f.type === "integer" ? " (must be a number)" : " (must be text, not a number)";
                    const val = String(entry[f.key] ?? "").trim();
                    const isPlaceholder = isQualityCheckedGroupField(groupKey, f) && val && isPlaceholderText(val);
                    missing.push({
                        path: `${pathKey}[${idx}].${f.key}`,
                        title: isPlaceholder
                            ? `${g.title}, entry #${idx + 1}, field "${f.title}" (current value is placeholder/filler text — write a real, specific feature/benefit)${typeHint}`
                            : `${g.title}, entry #${idx + 1}, field "${f.title}"${typeHint}`,
                        type: f.type, enum_values: f.enumValues,
                    });
                });
            });
        });

        contentChecks.buildAiTasks(currentValues, currentGroupValues).forEach((t) => {
            const existing = missing.findIndex((m) => m.path === t.path);
            if (existing >= 0) missing[existing] = t; else missing.push(t);
        });

        return missing;
    };

    // Drives the "Fill Missing with AI" button's disabled state — now covers
    // both plain scalar fields and required sub-fields on repeatable-group
    // entries (see computeMissingForAI above).
    const aiFillableMissingCount = useMemo(
        () => computeMissingForAI(values, groupValues, countryOfOrigin).length,
        [fields, values, groupFields, groupValues, conditionalRules, countryOfOrigin]
    );

    // Retries automatically (up to 3 passes) since a single AI call won't
    // always fill every requested field — this makes the button feel like
    // "one click, done" instead of requiring the user to notice gaps and
    // click again themselves.
    const markAiFilled = (paths) => {
        setAiFilledPaths((prev) => {
            const next = new Set(prev);
            paths.forEach((p) => next.add(p));
            return next;
        });
    };

    const handleFillWithAI = async () => {
        setAiLoading(true);
        try {
            let currentValues = values;
            let currentGroupValues = groupValues;
            let currentCountry = countryOfOrigin;

            // knownValues is often empty/partial on this flow (e.g. adding an
            // existing item to a WFS/SF batch never populates it) — fall back
            // to whatever's actually live on the form so the AI always has
            // real title/brand context instead of blank strings.
            const titleFieldPath = fields.find((f) => f.key === "productName")?.path.join(".");
            const brandFieldPath = fields.find((f) => f.key === "brand")?.path.join(".");
            const liveTitle = titleFieldPath ? currentValues[titleFieldPath] : null;
            const liveBrand = brandFieldPath ? currentValues[brandFieldPath] : null;
            const contextTitle = knownValues?.productName || liveTitle || "";
            const contextBrand = knownValues?.brand || liveBrand || "";

            for (let attempt = 0; attempt < 3; attempt++) {
                const missing = computeMissingForAI(currentValues, currentGroupValues, currentCountry);
                if (missing.length === 0) break;

                const resp = await api("/api/walmart/api/fill-missing-with-ai", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        product_context: {
                            title: contextTitle, brand: contextBrand,
                            category: productType, description: knownValues?.shortDescription || "",
                            facts: contentChecks.listingFacts(currentValues),
                            // Every filled-in attribute (piece count, age, material, capacity...)
                            // so AI-written copy is specific instead of generic filler.
                            attributes: Object.fromEntries(
                                fields
                                    .filter((f) => !/image/i.test(f.key) && !["shortDescription", "longDescription", "keyFeatures", "price"].includes(f.key))
                                    .map((f) => {
                                        const pk = f.path.join(".");
                                        const v = f.isMeasureUnit
                                            ? (currentValues[`${pk}.measure`] ? `${currentValues[`${pk}.measure`]} ${currentValues[`${pk}.unit`] || ""}`.trim() : "")
                                            : String(currentValues[pk] ?? "").trim();
                                        return [f.title, v];
                                    })
                                    .filter(([, v]) => v && v.length < 200)
                            ),
                            // Existing real (non-placeholder) key features, for context and to avoid duplicates.
                            key_features: (Object.entries(currentGroupValues).find(([k]) => k.endsWith("keyFeatures"))?.[1] || [])
                                .map((e) => String(e?.value ?? "").trim())
                                .filter((v) => v && !isPlaceholderText(v)),
                        },
                        missing_fields: missing,
                    }),
                });
                const data = await resp.json();
                if (!resp.ok || !data.values || Object.keys(data.values).length === 0) break; // no progress this round — stop retrying

                const sanitizedScalar = {};
                const filledGroupPaths = [];
                let nextGroupValues = currentGroupValues;
                Object.entries(data.values).forEach(([k, v]) => {
                    if (k === "__countryOfOrigin__") {
                        const strVal = (Array.isArray(v) ? v.join(",") : String(v ?? "")).trim();
                        const matched = COUNTRIES.find((c) => c.toLowerCase() === strVal.toLowerCase());
                        console.log("[AI country fill]", { received: strVal, matched });
                        if (matched) {
                            currentCountry = matched;
                            setCountryOfOrigin(matched);
                            markAiFilled(["__countryOfOrigin__"]);
                        }
                        return;
                    }
                    const groupMatch = k.match(GROUP_AI_PATH_RE);
                    if (groupMatch) {
                        const [, groupPathKey, idxStr, subKey] = groupMatch;
                        const idx = parseInt(idxStr, 10);
                        const arr = [...(nextGroupValues[groupPathKey] || [])];
                        if (!arr[idx]) return;

                        // Sanity-check the answer against this sub-field's OWN
                        // declared type/enum before writing it in — protects
                        // against the AI swapping values between two
                        // similarly-titled sibling fields on the same entry
                        // (e.g. writing a percentage number into "Material
                        // Name"). A number-typed field must parse as a number;
                        // a plain string field with no enum must NOT be a bare
                        // number (fabric/material names are never just digits).
                        const g = groupFields.find((gr) => gr.path.join(".") === groupPathKey);
                        const subField = g?.itemFields.find((sf) => sf.key === subKey);
                        const strVal = Array.isArray(v) ? v.join(",") : String(v ?? "");
                        const looksNumeric = /^-?\d+(\.\d+)?$/.test(strVal.trim());

                        if (subField) {
                            if ((subField.type === "number" || subField.type === "integer") && !looksNumeric) return;
                            if (subField.type !== "number" && subField.type !== "integer" && !subField.enumValues && looksNumeric) return;
                            if (subField.enumValues && !subField.enumValues.includes(strVal)) return;
                        }

                        arr[idx] = { ...arr[idx], [subKey]: strVal };
                        nextGroupValues = { ...nextGroupValues, [groupPathKey]: arr };
                        filledGroupPaths.push(`${groupPathKey}[${idx}].${subKey}`);
                    } else {
                        // Coerce to string immediately — the AI can return a
                        // JSON number (e.g. 0 for "Number of Unique Hazardous
                        // Components"), and storing a raw number instead of
                        // "0" means every later `rawValue || ""` check treats
                        // it as empty (0 is falsy), wrongly re-flagging a
                        // correctly-answered field as still missing.
                        let val = Array.isArray(v) ? v.join(",") : String(v ?? "");
                        if (k.endsWith(".unit")) {
                            const basePath = k.slice(0, -5);
                            const targetField = fields.find((ff) => ff.isMeasureUnit && ff.path.join(".") === basePath);
                            if (targetField?.unitEnum) {
                                const resolved = resolveEnumValue(targetField.unitEnum, val);
                                if (!resolved) return; // invalid unit answer — skip, leave field empty for manual fill
                                val = resolved;
                            }
                        } else if (!k.endsWith(".measure")) {
                            const targetField = fields.find((ff) => ff.path.join(".") === k);
                            if (targetField?.enumValues && !targetField.isMultiSelect && !targetField.isStringArray) {
                                const resolved = resolveEnumValue(targetField.enumValues, val);
                                if (!resolved) return; // invalid enum answer — skip, leave field empty for manual fill
                                val = resolved;
                            }
                        }
                        sanitizedScalar[k] = val;
                    }
                });

                currentValues = { ...currentValues, ...sanitizedScalar };
                currentGroupValues = nextGroupValues;
                setValues(currentValues);
                setGroupValues(currentGroupValues);
                markAiFilled([...Object.keys(sanitizedScalar), ...filledGroupPaths]);
            }

            // Safety net: if the AI never returned a usable replacement for a
            // placeholder field (bad response, retries exhausted, etc.), never
            // let the original filler text ("Feature Benefits not yet
            // available. Coming Soon!" and similar) silently survive a Fill
            // Missing with AI pass. Blank it instead — an empty required
            // field is caught by the normal missing-field flagging and can be
            // filled by hand, whereas leftover placeholder text looks
            // "filled" and would otherwise slip through to submission again.
            const clearedScalarPaths = [];
            fields.forEach((f) => {
                if (!isQualityCheckedField(f)) return;
                const pathKey = f.path.join(".");
                const val = String(currentValues[pathKey] ?? "").trim();
                if (val && isPlaceholderText(val)) {
                    currentValues = { ...currentValues, [pathKey]: "" };
                    clearedScalarPaths.push(pathKey);
                }
            });
            if (clearedScalarPaths.length > 0) setValues(currentValues);

            let groupValuesChanged = false;
            const clearedGroupPaths = [];
            groupFields.forEach((g) => {
                const groupKey = g.path[g.path.length - 1];
                const pathKey = g.path.join(".");
                const entries = currentGroupValues[pathKey] || [];
                const nextEntries = entries.map((entry, idx) => {
                    let entryChanged = false;
                    const nextEntry = { ...entry };
                    g.itemFields.forEach((f) => {
                        if (!isQualityCheckedGroupField(groupKey, f)) return;
                        const val = String(entry[f.key] ?? "").trim();
                        if (val && isPlaceholderText(val)) {
                            nextEntry[f.key] = "";
                            entryChanged = true;
                            clearedGroupPaths.push(`${pathKey}[${idx}].${f.key}`);
                        }
                    });
                    if (entryChanged) groupValuesChanged = true;
                    return entryChanged ? nextEntry : entry;
                });
                if (nextEntries !== entries) currentGroupValues = { ...currentGroupValues, [pathKey]: nextEntries };
            });
            if (groupValuesChanged) setGroupValues(currentGroupValues);

            // These paths are no longer AI-filled (they're blank again, not
            // AI content) — drop the purple "AI" badge so the red "missing"
            // state is what the seller sees, not a stale AI badge on an
            // empty field.
            if (clearedScalarPaths.length > 0 || clearedGroupPaths.length > 0) {
                setAiFilledPaths((prev) => {
                    const next = new Set(prev);
                    [...clearedScalarPaths, ...clearedGroupPaths].forEach((p) => next.delete(p));
                    return next;
                });
            }
        } catch {
            setError("AI fill failed — please fill remaining fields manually.");
        } finally {
            setAiLoading(false);
        }
    };

    const buildPayload = () => {
        const item = {};

        // Repeatable groups: build clean entry objects (drop empty sub-fields,
        // coerce numbers), then drop any entry that ended up fully empty.
        // Non-empty result -> write it. Empty result -> omit the key entirely
        // rather than submit [] — Walmart only rejects arrays that are
        // PRESENT with fewer entries than minItems; an absent optional key is
        // always fine.
        groupFields.forEach((g) => {
            const { gated, active } = gatingInfo(g.path);
            if (gated && !active) return; // condition not met — never included, regardless of any stale entries left in state

            const pathKey = g.path.join(".");
            const rawEntries = groupValues[pathKey] || [];

            if (g.isPlainStringArray) {
                // e.g. safetyDataSheet — Walmart wants ["url1", "url2"], not
                // [{value: "url1"}, {value: "url2"}].
                const cleanValues = rawEntries.map((e) => e.value).filter((v) => v && String(v).trim());
                if (cleanValues.length > 0) setDeep(item, g.path, cleanValues);
                return;
            }

            const cleanEntries = rawEntries
                .map((entry) => {
                    const cleaned = {};
                    g.itemFields.forEach((f) => {
                        const val = entry[f.key];
                        if (val === "" || val == null) return;
                        if (f.enumValues) {
                            const resolved = resolveEnumValue(f.enumValues, val);
                            if (!resolved) return; // invalid enum value — omit rather than submit and get rejected
                            cleaned[f.key] = resolved;
                            return;
                        }
                        cleaned[f.key] = f.type === "number" ? parseFloat(val)
                            : f.type === "integer" ? parseInt(val, 10)
                                : val;
                    });
                    return cleaned;
                })
                .filter((e) => Object.keys(e).length > 0);

            if (cleanEntries.length > 0) {
                setDeep(item, g.path, cleanEntries);
            }
        });

        // Array groups we don't have a UI for, but which Walmart's schema
        // accepts empty (minItems 0/absent) — submit [] so the key exists
        // rather than omitting it outright.
        skippedGroups.forEach((g) => {
            const groupKey = g.path[g.path.length - 1];
            if (SKIPPED_GROUP_DEFAULTS[groupKey]) {
                setDeep(item, g.path, SKIPPED_GROUP_DEFAULTS[groupKey]);
            } else if (!g.required) {
                // Not required at all — omit the key entirely. Walmart only
                // rejects arrays that are PRESENT with fewer than minItems
                // entries; an absent key is fine. This is what fixes Label
                // Image / External Product Identifier / Fabric Content /
                // Swatch Images, none of which are in this schema's
                // required[] list.
            } else if (!(g.minItems > 0)) {
                // Required, but no minimum entry count — empty array is valid.
                setDeep(item, g.path, []);
            }
            // else: required AND minItems > 0 with no default — this is a
            // real blocker, caught by blockingSkippedGroups above, and the
            // form won't let submission reach this point.
        });
        fields.forEach((f) => {
            const { gated, active } = gatingInfo(f.path);
            if (gated && !active) return; // condition not met — never included, regardless of any stale value left in state

            if (f.isMeasureUnit) {
                const measure = values[`${f.path.join(".")}.measure`];
                let unit = values[`${f.path.join(".")}.unit`];
                if (measure && unit) {
                    if (f.unitEnum) {
                        const resolvedUnit = resolveEnumValue(f.unitEnum, unit);
                        if (!resolvedUnit) return; // invalid unit — omit rather than submit garbage
                        unit = resolvedUnit;
                    }
                    setDeep(item, f.path, { measure: parseFloat(measure), unit });
                }
                return;
            }
            const raw = values[f.path.join(".")];
            if (raw === "" || raw == null) return;

            if (f.isMultiSelect) {
                const items = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
                const resolved = items.map((it) => resolveEnumValue(f.enumValues, it)).filter(Boolean);
                if (resolved.length > 0) setDeep(item, f.path, resolved);
                return;
            }
            if (f.enumValues && !f.isStringArray) {
                const resolved = resolveEnumValue(f.enumValues, raw);
                if (!resolved) return; // invalid enum value — omit rather than submit and get rejected
                setDeep(item, f.path, resolved);
                return;
            }
            const parsed = f.type === "number" ? parseFloat(raw)
                : f.type === "integer" ? parseInt(raw, 10)
                    : f.isStringArray ? String(raw).split(",").map((s) => s.trim()).filter(Boolean)
                        : raw;
            setDeep(item, f.path, parsed);
        });

        if (!item.Orderable) item.Orderable = {};
        item.Orderable.productIdentifiers = { productIdType: idType, productId: idValue.trim() };
        // Fill SKU into every container that independently requires it
        // (MP_ITEM: just Orderable. WFS: Orderable AND TradeItem both).
        skuPaths.forEach((p) => setDeep(item, p, sku.trim()));

        const resolvedCountry = COUNTRY_ALIASES[countryOfOrigin] || countryOfOrigin;
        if (countryOfOriginIsArray) {
            const code = COUNTRY_CODES[resolvedCountry];
            const formatted = code ? `${code} - ${resolvedCountry}` : resolvedCountry;
            setDeep(item, countryOfOriginPath, [formatted]);
        } else {
            setDeep(item, countryOfOriginPath, resolvedCountry);
        }

        return {
            [headerKey]: { businessUnit: "WALMART_US", locale: "en", version: version },
            [itemContainerKey]: [{ ...item }],
        };
    };

    const handleSubmit = async () => {
        // Belt-and-suspenders: the Add to Batch button is already disabled
        // while totalMissingCount > 0, but this makes it impossible for a
        // submission to slip through incomplete regardless of how that
        // happened (stale click, disabled attribute race, etc).
        if (totalMissingCount > 0) {
            setFinishError(`This item still has ${totalMissingCount} required field${totalMissingCount === 1 ? "" : "s"} missing — please fill ${totalMissingCount === 1 ? "it" : "them"} in before adding to the batch.`);
            return;
        }
        setFinishing(true);
        setFinishError("");
        try {
            const built = buildPayload();
            const nameKey = fields.find((f) => f.key === "productName")?.path.join(".");
            const liveTitle = nameKey ? values[nameKey] : null;
            recordSupplierUsed(supplier);
            await addBatchItem(batchId, {
                item_id: itemId,
                sku,
                title: liveTitle || knownValues?.productName || "",
                brand: knownValues?.brand || "",
                category: knownValues?.category || null,
                sub_category: knownValues?.sub_category || null,
                image_url: imageUrl,
                quantity: quantity ? parseInt(quantity, 10) : null,
                supplier,
                expiration_date: expirationDate || null,
                purchase_date: purchaseDate || null,
                feed_type: feedType,
                product_type: productType,
                fee_breakdown: {
                    referral_fee: profitEstimate.referralFee,
                    wfs_fee: profitEstimate.wfsFee,
                    fulfillment_cost: profitEstimate.fulfillmentCost,
                    inbound_shipping: profitEstimate.inboundShipping,
                    sales_tax: profitEstimate.salesTax,
                },
                total_fees: profitEstimate.unitFees,
                payload: built[itemContainerKey][0],
                feed_header: built[headerKey],
                costs: [
                    ...(costPriceDefId && costPrice !== "" && costPrice != null
                        ? [{ cost_definition_id: costPriceDefId, value: parseFloat(costPrice) }]
                        : []),
                    ...Object.entries(selectedCosts)
                        .filter(([id, v]) => id !== costPriceDefId && v !== "" && v != null)
                        .map(([cost_definition_id, value]) => ({ cost_definition_id, value: parseFloat(value) })),
                ],
                flag_definition_ids: selectedFlagIds,
                fields: Object.entries(selectedFields)
                    .filter(([, v]) => v !== "" && v != null)
                    .map(([field_definition_id, value]) => ({ field_definition_id, value })),
            });
            onAddToBatch();
        } catch (err) {
            setFinishError(err.message || "Failed to save item to batch");
            setFinishing(false);
        }
    };

    const profitEstimate = useMemo(() => {
        const priceFieldPath = fields.find((f) => f.key === "price")?.path.join(".");
        const salePrice = parseFloat(priceFieldPath ? values[priceFieldPath] : values["Orderable.price"]) || 0;

        const weightField = fields.find((f) => ["assembledProductWeight", "eachWeight", "ShippingWeight"].includes(f.key));
        const weight = weightField ? parseFloat(values[weightField.path.join(".")]) || null : null;

        const dimFor = (keys) => {
            const f = fields.find((ff) => keys.includes(ff.key));
            if (!f) return null;
            if (f.isMeasureUnit) return parseFloat(values[`${f.path.join(".")}.measure`]) || null;
            return parseFloat(values[f.path.join(".")]) || null;
        };
        const dimensions = {
            length: dimFor(["assembledProductLength", "assembledProductDepth", "eachDepth"]),
            width: dimFor(["assembledProductWidth", "eachWidth"]),
            height: dimFor(["assembledProductHeight", "eachHeight"]),
        };

        // Only actual WFS feed types get WFS fee / inbound shipping applied —
        // MP_ITEM_MATCH (an SF variant) must NOT be treated as WFS just
        // because it isn't literally "MP_ITEM".
        const isWFS = feedType === "MP_WFS_ITEM" || feedType === "OMNI_WFS";

        // Manual add-on costs (Prep Fee, COGS, etc) — excludes
        // costPriceDefId's own entry in selectedCosts since that duplicate
        // row is hidden from the UI and never authoritative (see
        // CostsPicker filter above). Kept separate from Cost Price because
        // sales tax only applies to the actual purchase price, not add-ons.
        const manualCostsSum = Object.entries(selectedCosts)
            .filter(([id, v]) => id !== costPriceDefId && v !== "" && v != null)
            .reduce((sum, [, v]) => sum + (parseFloat(v) || 0), 0);

        const est = estimateWalmartProfit({
            salePrice, costPrice: parseFloat(costPrice) || 0, extraCosts: manualCostsSum,
            weight, dimensions,
            category: knownValues?.category,
            subCategory: knownValues?.sub_category,
            isWFS,
        });

        const qty = Math.max(1, parseInt(quantity, 10) || 1);

        // Breakeven sell price + max cost price, solved against this same
        // fee engine so they always agree with the Profit/ROI shown below.
        const profitAt = (sale, cost) => estimateWalmartProfit({
            salePrice: sale, costPrice: cost, extraCosts: manualCostsSum,
            weight, dimensions,
            category: knownValues?.category,
            subCategory: knownValues?.sub_category,
            isWFS,
        });
        const costNum = parseFloat(costPrice) || 0;

        // Breakeven: lowest sell price where profit >= 0.
        let breakeven = null;
        {
            let lo = 0;
            let hi = Math.max(10, salePrice * 4, costNum * 4);
            if (profitAt(hi, costNum).profit >= 0) {
                for (let i = 0; i < 40; i++) {
                    const mid = (lo + hi) / 2;
                    if (profitAt(mid, costNum).profit >= 0) hi = mid; else lo = mid;
                }
                breakeven = hi;
            }
        }

        // Max cost: highest cost where profit >= GOOD_PROFIT AND ROI >= GOOD_ROI.
        let maxCost = null;
        if (salePrice > 0) {
            const meetsTargets = (c) => {
                const r = profitAt(salePrice, c);
                return r.profit >= GOOD_PROFIT && (c <= 0 || r.roi >= GOOD_ROI);
            };
            if (meetsTargets(0)) {
                let lo = 0;
                let hi = salePrice;
                for (let i = 0; i < 40; i++) {
                    const mid = (lo + hi) / 2;
                    if (meetsTargets(mid)) lo = mid; else hi = mid;
                }
                maxCost = lo;
            } else {
                maxCost = 0;
            }
        }

        return {
            ...est,
            isWFS,
            qty,
            breakeven,
            maxCost,
            unitProfit: est.profit,
            unitFees: est.totalFees,
            totalProfit: est.profit * qty,
            totalFees: est.totalFees * qty,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, values, costPrice, selectedCosts, costPriceDefId, productType, feedType, quantity, feeSettingsVersion]);

    const priceField = fields.find((f) => f.key === "price");
    const priceValue = priceField ? (values[priceField.path.join(".")] ?? "") : "";
    const salePriceForTotal = parseFloat(priceValue) || 0;
    const idProductIdError = validateProductId(idType, idValue);

    if (stage === "fetching_schema") {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-10 flex flex-col items-center text-gray-500 text-sm">
                {error ? (
                    <div className="text-center">
                        <AlertCircle size={24} className="text-red-400 mx-auto mb-2" />
                        <p className="text-red-600">{error}</p>
                        <button onClick={onBack} className="mt-4 text-teal-600 underline text-sm">Go Back</button>
                    </div>
                ) : (
                    <>Fetching field requirements for "{productType}"...</>
                )}
            </div>
        );
    }

    const sections = [...new Set(fields.map((f) => f.section))];
    const fieldClass = (missing, isAi) => `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none ${missing ? "border-2 border-red-600 bg-red-100 ring-2 ring-red-300 focus:border-red-700" : isAi ? "border-2 border-purple-400 bg-purple-50 focus:border-purple-500" : "border-gray-200 focus:border-teal-500"}`;

    const nameKey = fields.find((f) => f.key === "productName")?.path.join(".");
    const liveTitle = nameKey ? values[nameKey] : null;

    const AiBadge = () => (
        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded-full ml-1">
            <Wand2 size={9} /> AI
        </span>
    );

    return (
        <>
            <div ref={formRef} className="bg-white rounded-2xl shadow-lg p-6 pb-24">
                {skuModalOpen && (
                    <SkuBuilderModal
                        pattern={skuPattern}
                        ctx={buildSkuCtx()}
                        costDefs={costDefs}
                        flagDefs={flagDefs}
                        fieldDefs={fieldDefs}
                        onSave={handleSkuSave}
                        onClose={() => setSkuModalOpen(false)}
                    />
                )}

                <div className="flex items-start gap-3 mb-5 pb-5 border-b border-gray-100">
                    {imageEditable ? (
                        <button onClick={onImageChange} className="relative flex-shrink-0 group" type="button">
                            <ProductImage src={imageUrl} className={photosMissingCount > 0 ? "ring-2 ring-red-300 rounded" : ""} />
                            <div className="absolute inset-0 bg-black/40 rounded opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                                <span className="text-white text-[9px] font-bold">Change</span>
                            </div>
                            <span className={`absolute -bottom-2 -right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow whitespace-nowrap ${photosMissingCount === 0 ? "bg-purple-600 text-white" : "bg-red-500 text-white"}`}>
                                {!imageUrl ? "Add Photo *" : photosMissingCount > 0 ? `Add ${photosMissingCount} More Photo${photosMissingCount === 1 ? "" : "s"} *` : "Edit Photos"}
                            </span>
                        </button>
                    ) : (
                        <ProductImage src={imageUrl} />
                    )}
                    <div className="min-w-0 flex-1">
                        {titleEditable ? (
                            <input
                                value={liveTitle || ""}
                                onChange={(e) => nameKey && setFieldValue(nameKey.split("."), e.target.value)}
                                placeholder="Product title — required"
                                className="w-full font-bold text-gray-800 text-sm leading-snug border-b-2 border-dashed border-teal-300 focus:border-teal-500 focus:outline-none bg-transparent px-0.5 py-0.5"
                            />
                        ) : (
                            <p className="font-bold text-gray-800 text-sm leading-snug">
                                {knownValues?.productName || liveTitle || knownValues?.brand || "Change Name"}
                            </p>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5">
                            {knownValues?.brand} · {productType}
                            {itemId && (
                                <>
                                    {" · "}
                                    <a href={`https://www.walmart.com/ip/${itemId}`} target="_blank" rel="noopener noreferrer"
                                        className="text-teal-600 hover:underline inline-flex items-center gap-0.5">
                                        {itemId} <ExternalLink size={10} />
                                    </a>
                                </>
                            )}
                        </p>
                    </div>
                    <div className="ml-auto flex-shrink-0 flex flex-col items-end gap-1">
                        <button onClick={handleFillWithAI} disabled={aiLoading || aiFillableMissingCount === 0}
                            title={aiFillableMissingCount === 0 ? "Nothing left for AI to fill" : undefined}
                            className="px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold hover:bg-purple-100 transition flex items-center gap-1.5 disabled:opacity-50">
                            {aiLoading ? "Filling..." : <><Wand2 size={14} /> Fill Missing with AI</>}
                        </button>
                        <span className="text-[10px] text-gray-400">AI can make mistakes. Please double-check.</span>
                    </div>
                </div>

                <div id="missing-top" className="grid grid-cols-3 gap-3 mb-5">
                    <div className="p-4 bg-gray-50 rounded-xl">
                        <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                            Product ID {idProductIdError && <span className="text-red-500">*</span>}
                        </label>
                        <div className="flex gap-1 mb-1.5">
                            {PRODUCT_ID_TYPES.map((t) => (
                                <button key={t} onClick={() => setIdType(t)} type="button"
                                    className={`px-1.5 py-1 text-[10px] font-bold rounded border ${idType === t ? "border-teal-500 bg-teal-100 text-teal-700" : "border-gray-200 text-gray-400"}`}>
                                    {t}
                                </button>
                            ))}
                        </div>
                        <input value={idValue}
                            onChange={(e) => setIdValue(e.target.value.replace(/[^\d]/g, ""))}
                            maxLength={Math.max(...PRODUCT_ID_LENGTHS[idType])}
                            placeholder={`Enter ${idType} (${PRODUCT_ID_LENGTHS[idType].join(" or ")} digits)`}
                            className={`${fieldClass(!!idProductIdError)} font-mono`} />
                        {idProductIdError && idValue.trim() && (
                            <p className="text-[10px] text-red-500 mt-1">{idProductIdError}</p>
                        )}
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-semibold text-gray-600">SKU</label>
                            <button onClick={() => setSkuModalOpen(true)} className="text-xs text-teal-600 hover:text-teal-700">SKU Builder Settings</button>
                        </div>
                        <input
                            value={sku}
                            onChange={(e) => { setSku(e.target.value); setSkuManuallyEdited(true); }}
                            className={`${fieldClass(!sku.trim())} font-mono`}
                        />
                        {suggestedSku && (
                            <p className="text-[10px] text-orange-500 mt-1">
                                This is the SKU already used for this item - Walmart won't let you change the SKU of an item that already exists, so editing this may create an error.
                            </p>
                        )}
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                        <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                            Country of Origin {!countryOfOrigin.trim() && <span className="text-red-500">*</span>}{aiFilledPaths.has("__countryOfOrigin__") && <AiBadge />}
                        </label>
                        <CountryPicker value={countryOfOrigin} onChange={setCountryOfOrigin} />
                    </div>
                </div>

                {missingList.length > 0 && (
                    <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3 mb-5 text-xs text-red-700">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <p className="font-bold flex items-center gap-1.5">
                                <AlertCircle size={14} className="flex-shrink-0" />
                                Still missing ({missingList.length}) - fill these before you can add this item:
                            </p>
                            {aiFillableMissingCount > 0 && (
                                <button type="button" onClick={handleFillWithAI} disabled={aiLoading}
                                    title="We recommend letting AI fix these — review the results before adding"
                                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-purple-500 via-fuchsia-500 to-pink-500 shadow-md shadow-purple-200 animate-pulse hover:animate-none disabled:opacity-60 whitespace-nowrap">
                                    <Wand2 size={12} /> {aiLoading ? "Filling..." : "Recommended: fix with Fill Missing with AI"}
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {missingList.map((m, i) => (
                                <button key={i} type="button" onClick={() => scrollToMissing(m.id)}
                                    title="Click to jump to this field"
                                    className="bg-white border border-red-300 text-red-700 font-semibold px-2 py-1 rounded-full hover:bg-red-600 hover:text-white transition cursor-pointer">
                                    {m.label} ↓
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <ContentIssuesBanner checks={contentChecks} onJump={scrollToMissing} />

                {blockingSkippedGroups.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-xs text-red-700 flex items-start gap-2">
                        <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                        <span>Some fields for this product type ({blockingSkippedGroups.map((g) => g.title).join(", ")}) require at least one entry and aren't supported yet — please contact support.</span>
                    </div>
                )}

                <div className="mb-5 pt-5 border-b border-gray-100">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Your Data</h3>
                    {buylistFilled.size > 0 && (
                        <p className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3">
                            📋 {buylistFilled.size} field{buylistFilled.size === 1 ? "" : "s"} auto-filled from your Buy List. Double-check them before adding.
                        </p>
                    )}
                    <div id="missing-yourdata" className="grid grid-cols-3 gap-3 mb-4">
                        <div>
                            <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center">Quantity to Ship{buylistFilled.has("quantity") && <BuylistBadge />}</label>
                            <input type="number" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center">Cost Price ($){buylistFilled.has("costPrice") && <BuylistBadge />}</label>
                                <button type="button" onClick={() => setShowBuylistCheck(true)}
                                    disabled={!idValue.trim()}
                                    title={!idValue.trim() ? "Enter a Product ID (UPC/GTIN) first" : undefined}
                                    className="text-[10px] text-purple-600 hover:text-purple-700 font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
                                    Check Buy List
                                </button>
                            </div>
                            <input type="number" value={costPrice} onChange={(e) => setCostPrice(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                            {(profitEstimate.breakeven != null || profitEstimate.maxCost != null) && (
                                <p className="text-[10px] text-gray-400 mt-1">
                                    Breakeven: {profitEstimate.breakeven != null ? `$${profitEstimate.breakeven.toFixed(2)}` : "—"}
                                    {" | "}
                                    Max cost: {profitEstimate.maxCost != null ? `$${profitEstimate.maxCost.toFixed(2)}` : "—"}
                                </p>
                            )}
                            {showBuylistCheck && (
                                <BuylistPriceCheckModal
                                    searchId={idValue}
                                    onSelectPrice={(price) => { if (price) setCostPrice(String(price)); setShowBuylistCheck(false); }}
                                    onClose={() => setShowBuylistCheck(false)}
                                />
                            )}
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                Sell Price ($){priceField?.required && !String(priceValue).trim() && <span className="text-red-500">*</span>}{buylistFilled.has("price") && <BuylistBadge />}
                            </label>
                            <input type="number" value={priceValue}
                                onChange={(e) => priceField && setFieldValue(priceField.path, e.target.value)}
                                disabled={!priceField}
                                title={!priceField ? "This product type has no price field" : undefined}
                                className={`${fieldClass(priceField?.required && !String(priceValue).trim())} disabled:bg-gray-50 disabled:text-gray-400`} />
                            {knownValues?.price != null && (
                                <p className="text-[10px] text-gray-400 mt-1">Current BuyBox: ${knownValues.price}</p>
                            )}
                        </div>
                        <div className="col-span-2 bg-gray-50 border border-gray-200 rounded-xl p-3">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-gray-600">
                                    Estimated Profit / ROI {profitEstimate.qty > 1 && <span className="text-gray-400 font-normal">(per unit, ×{profitEstimate.qty} below)</span>}
                                </span>
                                <button type="button" onClick={() => setShowFeeSettings(true)}
                                    className="text-[10px] text-teal-600 hover:text-teal-700 font-semibold flex items-center gap-1">
                                    <Settings size={11} /> Fee Settings
                                </button>
                            </div>

                            <div className="grid grid-cols-3 gap-2 text-center mb-2">
                                <div className="relative group">
                                    <p className="text-[10px] text-gray-400 flex items-center justify-center gap-1 cursor-help">
                                        Fees <span className="underline decoration-dotted">(hover)</span>
                                    </p>
                                    <p className="font-bold text-sm text-red-600">${profitEstimate.unitFees.toFixed(2)}</p>

                                    <div className="hidden group-hover:block absolute z-20 top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-56 text-left">
                                        <p className="text-[10px] font-bold text-gray-500 uppercase mb-1.5">Fee Breakdown (per unit)</p>
                                        <div className="space-y-1 text-xs">
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Referral Fee</span>
                                                <span className="font-semibold text-gray-700">${profitEstimate.referralFee.toFixed(2)}</span>
                                            </div>
                                            {profitEstimate.isWFS && (
                                                <>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-500">WFS Fee</span>
                                                        <span className="font-semibold text-gray-700">${profitEstimate.wfsFee.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-500">Inbound Shipping</span>
                                                        <span className="font-semibold text-gray-700">${profitEstimate.inboundShipping.toFixed(2)}</span>
                                                    </div>
                                                </>
                                            )}
                                            {!profitEstimate.isWFS && profitEstimate.fulfillmentCost > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Fulfillment Cost</span>
                                                    <span className="font-semibold text-gray-700">${profitEstimate.fulfillmentCost.toFixed(2)}</span>
                                                </div>
                                            )}
                                            {profitEstimate.salesTax > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Sales Tax (on cost)</span>
                                                    <span className="font-semibold text-gray-700">${profitEstimate.salesTax.toFixed(2)}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between pt-1 border-t border-gray-100">
                                                <span className="text-gray-600 font-bold">Total</span>
                                                <span className="font-bold text-red-600">${profitEstimate.unitFees.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400">Profit</p>
                                    <p className={`font-bold text-sm ${profitEstimate.unitProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
                                        ${profitEstimate.unitProfit.toFixed(2)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400">ROI</p>
                                    <p className={`font-bold text-sm ${profitEstimate.roi >= 0 ? "text-purple-600" : "text-red-600"}`}>
                                        {profitEstimate.roi.toFixed(1)}%
                                    </p>
                                </div>
                            </div>

                            {profitEstimate.qty > 1 && (
                                <div className="grid grid-cols-4 gap-2 text-center pt-2 border-t border-gray-200">
                                    <div>
                                        <p className="text-[10px] text-gray-400">Total Cost (×{profitEstimate.qty})</p>
                                        <p className="font-bold text-sm text-gray-700">${(profitEstimate.totalCostBasis * profitEstimate.qty).toFixed(2)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-gray-400">Total Sell (×{profitEstimate.qty})</p>
                                        <p className="font-bold text-sm text-gray-700">${(salePriceForTotal * profitEstimate.qty).toFixed(2)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-gray-400">Total Fees (×{profitEstimate.qty})</p>
                                        <p className="font-bold text-sm text-red-600">${profitEstimate.totalFees.toFixed(2)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-gray-400">Total Profit (×{profitEstimate.qty})</p>
                                        <p className={`font-bold text-sm ${profitEstimate.totalProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
                                            ${profitEstimate.totalProfit.toFixed(2)}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                        {showFeeSettings && (
                            <WalmartFeeSettingsModal onClose={(saved) => {
                                setShowFeeSettings(false);
                                if (saved) setFeeSettingsVersion((v) => v + 1);
                            }} />
                        )}
                        <div>
                            <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center">Supplier{buylistFilled.has("supplier") && <BuylistBadge />}</label>
                            <SupplierPicker value={supplier} onChange={handleSupplierChange} />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center">Expiration Date (optional){buylistFilled.has("expirationDate") && <BuylistBadge />}</label>
                            <input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center">Purchase Date (optional){buylistFilled.has("purchaseDate") && <BuylistBadge />}</label>
                            <input type="date" value={purchaseDate} onChange={(e) => handlePurchaseDateChange(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                        </div>
                    </div>

                    <div className="mb-4">
                        <button type="button" onClick={() => setShowRepricer((v) => !v)}
                            className="text-xs font-bold text-teal-600 hover:text-teal-700 flex items-center gap-1">
                            ⚡ {showRepricer ? "Hide" : "Show"} Repricer Integration
                        </button>
                        {showRepricer && (
                            <div className="mt-3">
                                <FlashPricerComponent
                                    asin={sku || ""}
                                    platform="walmart"
                                    basicData={{
                                        cost_price: costPrice || "",
                                        fbm_fee: !profitEstimate.isWFS ? profitEstimate.fulfillmentCost || "" : "",
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <CostsPicker costDefs={costDefs.filter((d) => d.id !== costPriceDefId)} selectedCosts={selectedCosts} setSelectedCosts={setSelectedCosts} autoFilled={buylistFilled}
                            onAddDef={(def) => setCostDefs((prev) => [...prev, def])} />
                        <FlagsPicker flagDefs={flagDefs} selectedFlagIds={selectedFlagIds} setSelectedFlagIds={setSelectedFlagIds}
                            onAddDef={(def) => setFlagDefs((prev) => [...prev, def])} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <FieldsPicker fieldDefs={fieldDefs} selectedFields={selectedFields} setSelectedFields={setSelectedFields} autoFilled={buylistFilled}
                            onAddDef={(def) => setFieldDefs((prev) => [...prev, def])} />
                    </div>
                </div>

                {sections.map((section) => (
                    <div key={section} className="mb-5">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{SECTION_LABELS[section] || section}</h3>
                        <div className="grid grid-cols-2 gap-3">
                            {fields.filter((f) => {
                                if (f.section !== section || f.key === "mainImageUrl" || f.key === "productSecondaryImageURL" || f.key === "price") return false;
                                const { gated, active } = gatingInfo(f.path);
                                return !gated || active; // hidden entirely when its condition isn't met
                            }).map((f) => {
                                const pathKey = f.path.join(".");
                                const { gated: fGated, active: fActive } = gatingInfo(f.path);
                                const effectiveRequired = fGated ? fActive : f.required;

                                if (f.isMeasureUnit) {
                                    const measureVal = values[`${pathKey}.measure`] ?? "";
                                    const unitVal = values[`${pathKey}.unit`] ?? "";
                                    const isAiMeasure = aiFilledPaths.has(`${pathKey}.measure`);
                                    const isAiUnit = aiFilledPaths.has(`${pathKey}.unit`);
                                    return (
                                        <div key={pathKey} id={`missing-${pathKey}`} className="col-span-2 grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                                    <span className={!measureVal ? "text-red-600 font-bold" : ""}>{f.title} — Measure</span> {!measureVal && <span className="text-red-500">*</span>}{isAiMeasure && <AiBadge />}
                                                </label>
                                                <input type="number" value={measureVal}
                                                    onChange={(e) => setValues((v) => ({ ...v, [`${pathKey}.measure`]: e.target.value }))}
                                                    className={fieldClass(!measureVal, isAiMeasure)} />
                                            </div>
                                            <div>
                                                <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1"><span className={!unitVal ? "text-red-600 font-bold" : ""}>{f.title} — Unit</span>{isAiUnit && <AiBadge />}</label>
                                                <select value={unitVal}
                                                    onChange={(e) => setValues((v) => ({ ...v, [`${pathKey}.unit`]: e.target.value }))}
                                                    className={fieldClass(!unitVal, isAiUnit)}>
                                                    <option value="">Select...</option>
                                                    {(f.unitEnum || []).map((u) => <option key={u} value={u}>{u}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    );
                                }

                                const value = values[pathKey] ?? "";
                                const invalidEnumValue = f.enumValues && !f.isMultiSelect && value && !f.enumValues.includes(value);
                                const isPlaceholderValue = isQualityCheckedField(f) && String(value).trim() && isPlaceholderText(String(value));
                                const missing = (effectiveRequired && (!String(value).trim() || invalidEnumValue)) || isPlaceholderValue;
                                const isScalarImageField = !f.isMultiSelect && !f.enumValues && (/image/i.test(f.key) || /image/i.test(f.title || ""));
                                const isAi = aiFilledPaths.has(pathKey);

                                return (
                                    <div key={pathKey} id={`missing-${pathKey}`} className={isScalarImageField ? "col-span-2" : ""}>
                                        <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                            <span className={missing ? "text-red-600 font-bold" : ""}>{f.title}</span>{effectiveRequired && <span className="text-red-500">*</span>}{isAi && <AiBadge />}
                                            {isPlaceholderValue && <span className="text-[9px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">Placeholder text</span>}
                                        </label>
                                        {f.isMultiSelect ? (
                                            <div className={`border rounded-lg px-3 py-2 text-sm max-h-32 overflow-y-auto ${missing ? "border-2 border-red-600 bg-red-100 ring-2 ring-red-300" : isAi ? "border-purple-300 bg-purple-50" : "border-gray-200"}`}>
                                                {(f.enumValues || []).map((v) => {
                                                    const selected = value ? value.split(",") : [];
                                                    const checked = selected.includes(v);
                                                    return (
                                                        <label key={v} className="flex items-center gap-2 py-0.5 cursor-pointer">
                                                            <input type="checkbox" checked={checked}
                                                                onChange={(e) => {
                                                                    const next = e.target.checked ? [...selected, v] : selected.filter((s) => s !== v);
                                                                    setFieldValue(f.path, next.join(","));
                                                                }} />
                                                            <span>{v}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        ) : f.enumValues ? (
                                            <select value={value} onChange={(e) => setFieldValue(f.path, e.target.value)} className={fieldClass(missing, isAi)}>
                                                <option value="">Select...</option>
                                                {f.enumValues.map((v) => <option key={v} value={v}>{v}</option>)}
                                            </select>
                                        ) : isScalarImageField ? (
                                            <div className="flex items-center gap-2">
                                                <div className={`w-12 h-12 rounded-lg overflow-hidden shrink-0 flex items-center justify-center border ${missing ? "border-2 border-red-600 bg-red-100 ring-2 ring-red-300" : "border-gray-200 bg-gray-50"}`}>
                                                    {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <span className="text-gray-300 text-lg">📷</span>}
                                                </div>
                                                <button type="button" onClick={() => openUpload({ kind: "scalar", path: f.path })} disabled={uploading}
                                                    className="px-3 py-2 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold whitespace-nowrap disabled:opacity-50 flex items-center gap-1">
                                                    {uploading && uploadTarget?.kind === "scalar" && uploadTarget.path.join(".") === pathKey
                                                        ? <><Loader2 size={12} className="animate-spin" /> Uploading...</>
                                                        : <><Upload size={12} /> {value ? "Replace" : "Upload"}</>}
                                                </button>
                                                {productImages.length > 0 && (
                                                    <button type="button" onClick={() => setImagePicker({ scalarPath: f.path })}
                                                        className="px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold whitespace-nowrap">
                                                        Pick Image
                                                    </button>
                                                )}
                                                {value && (
                                                    <button type="button" onClick={() => setFieldValue(f.path, "")} className="text-gray-400 hover:text-red-500">
                                                        <X size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <input type={f.type === "number" ? "number" : "text"} value={value}
                                                onChange={(e) => setFieldValue(f.path, e.target.value)}
                                                placeholder={f.isStringArray ? "comma, separated, values" : ""}
                                                className={fieldClass(missing, isAi)} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {section === "Visible" && fields.some((f) => f.key === "productSecondaryImageURL") && (() => {
                            const secondaryField = fields.find((f) => f.key === "productSecondaryImageURL");
                            const pathKey = secondaryField.path.join(".");
                            const currentUrls = (values[pathKey] || "").split(",").filter(Boolean);
                            const addUrl = () => openUpload({ kind: "secondary-add", pathKey });
                            const removeUrl = (idx) => {
                                setValues((v) => ({ ...v, [pathKey]: currentUrls.filter((_, i) => i !== idx).join(",") }));
                            };
                            return (
                                <div id="missing-photos" className="mt-4 pt-4 border-t border-gray-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-xs font-bold text-gray-600">Additional Images</h4>
                                        <span className="text-[10px] text-gray-400">
                                            {photosMissingCount > 0 ? `${photosMissingCount} more needed (0 or 3+ required)` : "Optional"}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {currentUrls.map((url, idx) => (
                                            <div key={idx} className="relative">
                                                <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                                                <button type="button" onClick={() => removeUrl(idx)}
                                                    className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5">
                                                    <X size={10} />
                                                </button>
                                            </div>
                                        ))}
                                        {productImages.filter((u) => u !== imageUrl && !currentUrls.includes(u)).length > 0 && (
                                            <button type="button" onClick={() => setImagePicker({ scalarPath: null, appendPath: pathKey })}
                                                className="w-16 h-16 border-2 border-dashed border-purple-300 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600 hover:bg-purple-100 transition">
                                                <Wand2 size={16} />
                                            </button>
                                        )}
                                        <button type="button" onClick={addUrl} disabled={uploading}
                                            title="Upload a photo"
                                            className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 hover:border-teal-400 hover:text-teal-600 transition disabled:opacity-50">
                                            {uploading && uploadTarget?.kind === "secondary-add" ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}

                        {groupFields.filter((g) => {
                            if (g.section !== section) return false;
                            const { gated, active } = gatingInfo(g.path);
                            return !gated || active; // hidden entirely when its condition isn't met (e.g. Label Image unless Chemical/Aerosol/Pesticide = Yes)
                        }).map((g) => {
                            const pathKey = g.path.join(".");
                            const entries = groupValues[pathKey] || [];
                            const { gated: gGated, active: gActive } = gatingInfo(g.path);
                            const gEffectiveRequired = gGated ? gActive : g.required;
                            const groupKeyName = g.path[g.path.length - 1];
                            const isSafetyDataSheet = groupKeyName === "safetyDataSheet";
                            // Best available identifier to search wercsmart with —
                            // UPC/GTIN is what that tool actually indexes by, so prefer
                            // it over the product title whenever we have one.
                            const sdsSearchTerm = idValue?.trim() || knownValues?.productName || "";

                            return (
                                <div key={pathKey} id={`missing-${pathKey}`} className="mt-4 pt-4 border-t border-gray-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="text-xs font-bold text-gray-600 flex items-center gap-1">
                                            {g.title}{gEffectiveRequired && g.minItems > 0 && <span className="text-red-500">*</span>}
                                        </h4>
                                        <span className="text-[10px] text-gray-400">
                                            {gGated ? "Required — condition met" : gEffectiveRequired && g.minItems > 0 ? `Requires at least ${g.minItems} entr${g.minItems === 1 ? "y" : "ies"}` : "Optional"}
                                        </span>
                                    </div>
                                    {entries.map((entry, idx) => (
                                        <div key={idx} className="border border-gray-200 rounded-xl p-3 mb-2">
                                            <div className="grid grid-cols-2 gap-2">
                                                {g.itemFields.map((f) => {
                                                    const val = entry[f.key] ?? "";
                                                    // Catches both "...ImageURL"-style keys (labelImageURL) and plain
                                                    // "...Image"-style keys some schemas use instead (e.g. an
                                                    // "Ingredient List Image" field with no "URL" in its key/title) —
                                                    // matched against both the key and the human-readable title.
                                                    // enumValues fields are closed-list selects (e.g. labelImageContains,
                                                    // whose key/title happen to contain "image" as a substring) — never
                                                    // treat those as image-URL fields regardless of naming.
                                                    const isImageField = !f.enumValues && (/image/i.test(f.key) || /image/i.test(f.title || ""));
                                                    const groupKey = g.path[g.path.length - 1];
                                                    const urlRequiredHere = f.key !== "labelImageURL" || isUrlRequiredForEntry(groupKey, entry);
                                                    const effectiveFieldRequired = f.required && urlRequiredHere;
                                                    const invalidEnumValue = f.enumValues && val && !f.enumValues.includes(val);
                                                    const isPlaceholderValue = isQualityCheckedGroupField(groupKey, f) && String(val).trim() && isPlaceholderText(String(val));
                                                    const missing = (effectiveFieldRequired && (!String(val).trim() || invalidEnumValue)) || isPlaceholderValue;
                                                    const isAiGroup = aiFilledPaths.has(`${pathKey}[${idx}].${f.key}`);
                                                    return (
                                                        <div key={f.key} className={isImageField ? "col-span-2" : ""}>
                                                            <label className="text-[10px] font-semibold text-gray-600 mb-1 flex items-center gap-1">
                                                                <span className={missing ? "text-red-600 font-bold" : ""}>{f.title}</span>{effectiveFieldRequired && <span className="text-red-500">*</span>}{isAiGroup && <AiBadge />}
                                                                {isPlaceholderValue && <span className="text-[9px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">Placeholder</span>}
                                                            </label>
                                                            {isImageField ? (
                                                                <div className="flex items-center gap-2">
                                                                    <div className={`w-12 h-12 rounded-lg overflow-hidden shrink-0 flex items-center justify-center border ${missing ? "border-2 border-red-600 bg-red-100 ring-2 ring-red-300" : "border-gray-200 bg-gray-50"}`}>
                                                                        {val ? <img src={val} alt="" className="w-full h-full object-cover" /> : <span className="text-gray-300 text-base">📷</span>}
                                                                    </div>
                                                                    <button type="button" onClick={() => openUpload({ kind: "group", pathKey, idx, subKey: f.key })} disabled={uploading}
                                                                        className="px-3 py-2 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold whitespace-nowrap disabled:opacity-50 flex items-center gap-1">
                                                                        {uploading && uploadTarget?.kind === "group" && uploadTarget.pathKey === pathKey && uploadTarget.idx === idx && uploadTarget.subKey === f.key
                                                                            ? <><Loader2 size={12} className="animate-spin" /> Uploading...</>
                                                                            : <><Upload size={12} /> {val ? "Replace" : "Upload"}</>}
                                                                    </button>
                                                                    {productImages.length > 0 && (
                                                                        <button type="button" onClick={() => setImagePicker({ pathKey, idx, subKey: f.key })}
                                                                            className="px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold whitespace-nowrap">
                                                                            Pick Image
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ) : isSafetyDataSheet ? (
                                                                <div>
                                                                    <div className="flex gap-2">
                                                                        <input type="text" value={val}
                                                                            onChange={(e) => setGroupEntryField(pathKey, idx, f.key, e.target.value)}
                                                                            placeholder="Paste SDS / battery spec document URL"
                                                                            className={`${fieldClass(missing, isAiGroup)} flex-1`} />
                                                                        <button type="button" onClick={() => triggerSdsSearch(sdsSearchTerm)}
                                                                            disabled={!sdsSearchTerm}
                                                                            title={sdsSearchTerm ? `Search wercsmart for "${sdsSearchTerm}"` : "Enter a Product ID first"}
                                                                            className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold whitespace-nowrap transition disabled:opacity-40 shadow-sm">
                                                                            Get SDS
                                                                        </button>
                                                                    </div>
                                                                    <p className="text-[10px] text-green-600 mt-1">
                                                                        We'll copy the Product ID to your clipboard — just paste it into the search box.
                                                                    </p>
                                                                </div>
                                                            ) : f.enumValues ? (
                                                                <select value={val} onChange={(e) => setGroupEntryField(pathKey, idx, f.key, e.target.value)} className={fieldClass(missing, isAiGroup)}>
                                                                    <option value="">Select...</option>
                                                                    {f.enumValues.map((v) => <option key={v} value={v}>{v}</option>)}
                                                                </select>
                                                            ) : (
                                                                <input type={f.type === "number" || f.type === "integer" ? "number" : "text"} value={val}
                                                                    onChange={(e) => setGroupEntryField(pathKey, idx, f.key, e.target.value)}
                                                                    className={fieldClass(missing, isAiGroup)} />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <button onClick={() => removeGroupEntry(pathKey, idx)} type="button"
                                                className="mt-2 text-[10px] text-red-500 hover:text-red-600 font-semibold">
                                                Remove Entry
                                            </button>
                                        </div>
                                    ))}
                                    <button onClick={() => addGroupEntry(pathKey)} type="button"
                                        className="text-xs text-teal-600 hover:text-teal-700 font-semibold">
                                        + Add Entry
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ))}

                <input ref={uploadInputRef} type="file" accept="image/*" hidden onChange={handleFileSelected} />

                {imagePicker && (
                    <ImagePickerModal
                        images={imagePicker.appendPath ? productImages.filter((u) => u !== imageUrl) : productImages}
                        onSelect={(url) => {
                            if (imagePicker.appendPath) {
                                setValues((v) => {
                                    const existing = (v[imagePicker.appendPath] || "").split(",").filter(Boolean);
                                    if (existing.includes(url)) return v;
                                    return { ...v, [imagePicker.appendPath]: [...existing, url].join(",") };
                                });
                            } else if (imagePicker.scalarPath) {
                                setFieldValue(imagePicker.scalarPath, url);
                            } else {
                                setGroupEntryField(imagePicker.pathKey, imagePicker.idx, imagePicker.subKey, url);
                            }
                            setImagePicker(null);
                        }}
                        onClose={() => setImagePicker(null)}
                    />
                )}

                {(error || finishError) && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700 text-sm">
                        <AlertCircle size={16} /> {error || finishError}
                    </div>
                )}

            </div>
            {barRect && (
                <div className="fixed bottom-0 z-40 pb-4 pt-3 px-1"
                    style={{ left: barRect.left, width: barRect.width }}>
                    <button onClick={handleSubmit} disabled={totalMissingCount > 0 || finishing}
                        className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition shadow-lg disabled:opacity-40">
                        {totalMissingCount > 0
                            ? [
                                requiredMissingCount > 0 && `Fill ${requiredMissingCount} More Field${requiredMissingCount === 1 ? "" : "s"}`,
                                photosMissingCount > 0 && `Add ${photosMissingCount} More Photo${photosMissingCount === 1 ? "" : "s"}`,
                                groupMissingCount > 0 && `Fill ${groupMissingCount} Group Entry Field${groupMissingCount === 1 ? "" : "s"}`,
                                contentChecks.issueCount > 0 && `Fix ${contentChecks.issueCount} Content Issue${contentChecks.issueCount === 1 ? "" : "s"}`,
                                blockingSkippedGroups.length > 0 && "Contact Support Required",
                            ].filter(Boolean).join(" and ")
                            : finishing ? "Adding to Batch..." : "Add to Batch"}
                    </button>
                </div>
            )}
        </>
    );
};

export default WalmartItemForm;